/* =============================================================
   ISLA BELLA — BESPOKE RING BUILDER  |  builder.js
   Version: 7.4  |  June 2026
   Architecture rebuild for 64-product / 576-variant Shopify layout.

   ┌──────────────────────────────────────────────────────────┐
   │ ⚠️  DEPLOYMENT ORDER — LOCKED RULE — DO NOT VIOLATE  ⚠️   │
   ├──────────────────────────────────────────────────────────┤
   │ This file fetches /products/{handle}.js for all 64 builder│
   │ handles at boot. /products/{handle}.js ONLY resolves when │
   │ the product is BOTH:                                      │
   │   (a) Status: ACTIVE (not Draft, not Archived)            │
   │   (b) Available on the "Online Store" sales channel       │
   │                                                           │
   │ If even one of the 64 handles is Draft or unpublished from│
   │ Online Store at deploy time, this builder will fail every │
   │ visitor's first fetch → CASE A blocked overlay → no sales.│
   │                                                           │
   │ MANDATORY DEPLOYMENT SEQUENCE:                            │
   │   1. Import 576-variant CSV as Draft.                     │
   │   2. Verify 64 products + 576 variants in Shopify admin.  │
   │   3. Make ALL 64 builder products Active AND publish to   │
   │      Online Store sales channel.                          │
   │   4. Confirm /products/{handle}.js resolves for sample    │
   │      handles (curl or browser, expect 200 + variant JSON).│
   │   5. Duplicate live theme as rollback BEFORE any paste.   │
   │      Then paste + save Liquid v7.0 first, then            │
   │      immediately paste + save this builder.js. Gap between│
   │      saves: seconds, not minutes. See DEPLOYMENT_RUNBOOK. │
   │   6. Test the live builder end-to-end.                    │
   │   7. Archive old builder products (NOT delete) only after │
   │      all tests pass.                                      │
   │                                                           │
   │ NEVER deploy this code while builder products are Draft.  │
   └──────────────────────────────────────────────────────────┘

   Changes from 7.0:

     A. STRICT MEDIA RESOLVER  (locked: "Accuracy over coverage")
        ibGetMedia rewritten to a strict 2-tier matcher:
          1. Exact band+setting+shape (+metal if available)
          2. Exact band+setting+shape (no metal qualifier)
          → otherwise show neutral default. NEVER cross-band,
            cross-setting, or cross-shape "close-enough" matches.
        ibSetDefaultPreview simplified — no probing for arbitrary
        "nice-looking" defaults (e.g. solitaire+oval). Hero default
        only.
        ibMetalTint now suppresses tinting when the displayed layer
        is the neutral default — never recolour the brand opening.

     B. BATCHED FETCH WITH THROTTLING FALLBACK  (locked perf safeguard)
        Stage 1: parallel fetch of all 64 handles (fastest path).
        Stage 2: if any handle fails Stage 1's per-handle retry, the
                 failed subset is retried in batches of 16 sequential,
                 with a 250ms pause between batches to ease rate
                 limits. Successful Stage 1 results are preserved.
        If anything fails both stages → CASE A (blocked overlay).

   Changes from 6.4 (carried forward from 7.0 — data layer only):

     1. PRODUCT_HANDLES is now COMPUTED by convention from the
        locked structural axes (2 bands × 4 settings × 8 shapes),
        producing 64 handles. No Customiser fields needed.

        Convention: isla-bella-{band}-{setting}-{shape}

     2. BAND_ROUTING table — DELETED. Band is now a real Shopify
        product axis, encoded directly in the handle. No more
        UI-setting-to-Shopify-setting routing indirection.

     3. ibParseOption1() — REPLACED by ibParseCarat(). Variants
        now have only Carat in option1 and Metal in option2.

     4. ibRouteShopifySettingToBandUi() — DELETED. No longer
        needed; handle parsing produces band/setting/shape
        directly.

     5. ibBuildMaps() — REWRITTEN. Now derives band/setting/shape
        from the product handle (parsed via ibParseHandle), then
        reads carat from option1 and metal from option2. The
        5-part map key is unchanged: band|setting|shape|carat|metal,
        so every downstream consumer (sections 3–16) is untouched.

     6. ibPreflightCheck() — UPDATED for 64-handle / 576-variant
        expectation, with per-(band+setting) family reporting.

     7. ibReportMissingSKUs() — UPDATED to iterate the new
        576-combination space and report holes by family.

   Trilogy wording lock (Option 2 — confirmed by Ben):
     - Customer label: "Trilogy"
     - Internal key:   three-stone
     - SKU code:       TRI
     - Handle token:   three-stone

   Pricing model: PRICES COME FROM SHOPIFY VARIANT DATA. The
   pricing block in the Liquid IB_SCHEMA is dead in v6.4 and
   removed in v7.x of the Liquid file. This file does not
   compute prices.

   Two distinct failure cases — kept strictly separate
   (unchanged from v6.4):

     CASE A — SYSTEM: any product fetch fails BOTH parallel stage
                      and the batched retry stage.
       → Builder cannot trust its data
       → Loading overlay turns into blocked overlay
       → Cart CTAs disabled
       → Customer asked to refresh

     CASE B — SALES: customer picks a combination that has no
                     live Shopify variant
       → All fetches succeeded; maps are trusted
       → Buy Now routes to concierge with selection pre-filled
       → Not a failure — just a sales fallback

   Choice type classification (unchanged):
     A. VARIANT-DEFINING:  band, setting, shape, carat, metal
     B. ORDER-PROPERTY:    ring size, engraving (future)
     C. ADD-ON PRODUCT:    gift wrap, premium box (future)
   ============================================================= */

(function () {
  'use strict';

  if (!window.IB_SCHEMA) {
    console.error('[IB Builder] window.IB_SCHEMA not found. Check section load order.');
    return;
  }

  /* ── SVG icons for bands, settings and shapes ─────────────────────────── */
  var BAND_SVGS = {
    'plain': '<svg viewBox="0 0 48 48" fill="none"><ellipse cx="24" cy="28" rx="16" ry="5" stroke="#B89A6A" stroke-width="1.4" fill="rgba(184,154,106,0.05)"/><ellipse cx="24" cy="26" rx="16" ry="5" stroke="#B89A6A" stroke-width="1.4" fill="rgba(184,154,106,0.12)"/></svg>',
    'pave':  '<svg viewBox="0 0 48 48" fill="none"><ellipse cx="24" cy="28" rx="16" ry="5" stroke="#B89A6A" stroke-width="1.4" fill="rgba(184,154,106,0.05)"/><ellipse cx="24" cy="26" rx="16" ry="5" stroke="#B89A6A" stroke-width="1.4" fill="rgba(184,154,106,0.12)"/><circle cx="14" cy="25" r="1.1" fill="#B89A6A"/><circle cx="18" cy="24" r="1.1" fill="#B89A6A"/><circle cx="22" cy="23.5" r="1.1" fill="#B89A6A"/><circle cx="26" cy="23.5" r="1.1" fill="#B89A6A"/><circle cx="30" cy="24" r="1.1" fill="#B89A6A"/><circle cx="34" cy="25" r="1.1" fill="#B89A6A"/></svg>'
  };

  /* SETTING_SVGS keyed by INTERNAL key.
     Note: 'three-stone' is the new internal key per Trilogy lock.
     The customer-facing word is still 'Trilogy' (set in the Liquid
     IB_SCHEMA.settings array's `label` field). */
  var SETTING_SVGS = {
    'solitaire':    '<svg viewBox="0 0 48 48" fill="none"><polygon points="24,6 34,16 30,36 18,36 14,16" stroke="#B89A6A" stroke-width="1.4" fill="rgba(184,154,106,0.07)"/><polygon points="24,6 30,13 24,11 18,13" stroke="#B89A6A" stroke-width="0.9"/><line x1="24" y1="36" x2="24" y2="43" stroke="#B89A6A" stroke-width="1.4"/><line x1="18" y1="40" x2="30" y2="40" stroke="#B89A6A" stroke-width="1.4"/></svg>',
    'hidden-halo':  '<svg viewBox="0 0 48 48" fill="none"><polygon points="24,6 34,16 30,34 18,34 14,16" stroke="#B89A6A" stroke-width="1.4" fill="rgba(184,154,106,0.07)"/><ellipse cx="24" cy="34" rx="9" ry="3" stroke="#B89A6A" stroke-width="0.9" stroke-dasharray="2 1.5"/><line x1="24" y1="37" x2="24" y2="43" stroke="#B89A6A" stroke-width="1.4"/><line x1="18" y1="40" x2="30" y2="40" stroke="#B89A6A" stroke-width="1.4"/></svg>',
    'halo':         '<svg viewBox="0 0 48 48" fill="none"><polygon points="24,10 31,17 28,29 20,29 17,17" stroke="#B89A6A" stroke-width="1.3" fill="rgba(184,154,106,0.07)"/><circle cx="24" cy="20" r="12" stroke="#B89A6A" stroke-width="0.9" stroke-dasharray="1.5 2"/><line x1="24" y1="32" x2="24" y2="43" stroke="#B89A6A" stroke-width="1.3"/><line x1="18" y1="38" x2="30" y2="38" stroke="#B89A6A" stroke-width="1.3"/></svg>',
    'three-stone':  '<svg viewBox="0 0 56 48" fill="none"><polygon points="28,5 37,14 34,31 22,31 19,14" stroke="#B89A6A" stroke-width="1.4" fill="rgba(184,154,106,0.08)"/><polygon points="10,13 16,19 14,30 6,30 4,19" stroke="#B89A6A" stroke-width="1" fill="rgba(184,154,106,0.05)"/><polygon points="46,13 52,19 50,30 42,30 40,19" stroke="#B89A6A" stroke-width="1" fill="rgba(184,154,106,0.05)"/><line x1="28" y1="31" x2="28" y2="43" stroke="#B89A6A" stroke-width="1.4"/><line x1="22" y1="39" x2="34" y2="39" stroke="#B89A6A" stroke-width="1.4"/></svg>'
  };

  var SHAPE_SVGS = {
    'round':    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-label="Round diamond icon"><g stroke="#B8975A" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="32" cy="32" r="18"/><polygon points="32,16 40,19 46,24 48,32 46,40 40,45 32,48 24,45 18,40 16,32 18,24 24,19"/><path d="M32 16v32M16 32h32M24 19l8 13 8-13M18 24l14 8-14 8M46 24l-14 8 14 8M24 45l8-13 8 13"/></g></svg>',
    'oval':     '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-label="Oval diamond icon"><g stroke="#B8975A" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="32" cy="32" rx="14" ry="20"/><polygon points="32,14 38,18 42,24 43,32 42,40 38,46 32,50 26,46 22,40 21,32 22,24 26,18"/><path d="M32 14v36M21 32h22M26 18l6 14 6-14M22 24l10 8-10 8M42 24l-10 8 10 8M26 46l6-14 6 14"/></g></svg>',
    'emerald':  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-label="Emerald diamond icon"><g stroke="#B8975A" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polygon points="24,14 40,14 50,24 50,40 40,50 24,50 14,40 14,24"/><polygon points="28,18 36,18 46,28 46,36 36,46 28,46 18,36 18,28"/><rect x="27" y="27" width="10" height="10"/><path d="M24 14l8 13 8-13M14 24l13 8-13 8M50 24l-13 8 13 8M24 50l8-13 8 13M27 27l-9 1M37 27l9 1M27 37l-9-1M37 37l9-1"/></g></svg>',
    'radiant':  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-label="Radiant diamond icon"><g stroke="#B8975A" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polygon points="24,14 40,14 50,24 50,40 40,50 24,50 14,40 14,24"/><polygon points="32,18 40,22 46,32 40,42 32,46 24,42 18,32 24,22"/><rect x="26.5" y="26.5" width="11" height="11" transform="rotate(45 32 32)"/><path d="M24 14l8 18 8-18M14 24l18 8-18 8M50 24l-18 8 18 8M24 50l8-18 8 18M24 22l8 10 8-10M24 42l8-10 8 10"/></g></svg>',
    'pear':     '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-label="Pear diamond icon"><g stroke="#B8975A" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M32 12C38 18 44 24 44 34C44 43 38 50 32 52C26 50 20 43 20 34C20 24 26 18 32 12Z"/><path d="M32 12V52M24 24l8 10 8-10M20 34h24M24 44l8-10 8 10M22 28l10 6-10 8M42 28l-10 6 10 8"/></g></svg>',
    'cushion':  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-label="Cushion diamond icon"><g stroke="#B8975A" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M24 14H40L50 24V40L40 50H24L14 40V24L24 14Z"/><path d="M27 18h10l9 9v10l-9 9H27l-9-9V27l9-9Z"/><path d="M32 18v28M18 32h28M27 18l5 14 5-14M18 27l14 5-14 5M46 27l-14 5 14 5M27 46l5-14 5 14"/></g></svg>',
    'princess': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-label="Princess diamond icon"><g stroke="#B8975A" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="16" y="16" width="32" height="32"/><rect x="22" y="22" width="20" height="20"/><path d="M32 16v32M16 32h32M16 16l16 16 16-16M16 48l16-16 16 16M22 22l10 10 10-10M22 42l10-10 10 10"/></g></svg>',
    'marquise': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-label="Marquise diamond icon"><g stroke="#B8975A" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M32 14C40 18 46 24 48 32C46 40 40 46 32 50C24 46 18 40 16 32C18 24 24 18 32 14Z"/><path d="M32 14v36M16 32h32M22 24l10 8 10-8M22 40l10-8 10 8M18 28l14 4-14 4M46 28l-14 4 14 4"/></g></svg>'
  };

  /* ── Locked structural axes ──────────────────────────────────────────────
     These mirror the Liquid IB_SCHEMA but are needed in JS at boot time
     to compute the 64 product handles BEFORE the products are fetched.
     Source of truth for handles/keys/codes is here + the CSV generator. */
  var BAND_KEYS    = ['plain', 'pave'];
  var SETTING_KEYS = ['solitaire', 'hidden-halo', 'halo', 'three-stone'];
  var SHAPE_KEYS   = ['round', 'oval', 'emerald', 'radiant', 'pear', 'cushion', 'princess', 'marquise'];
  var METAL_KEYS   = ['wg', 'yg', 'pt'];

  var BANDS = (window.IB_SCHEMA.bands || [
    { key: 'plain', label: 'Plain Band', sub: 'Clean & minimal',      priceAdj: 0, swatchUrl: '', videoUrl: '' },
    { key: 'pave',  label: 'Pavé Band',  sub: 'Encrusted brilliance', priceAdj: 0, swatchUrl: '', videoUrl: '' }
  ]).map(function (b) { return Object.assign({}, b, { svg: BAND_SVGS[b.key] || '' }); });

  var SETTINGS  = (window.IB_SCHEMA.settings || []).map(function (s) { return Object.assign({}, s, { svg: SETTING_SVGS[s.key] || '' }); });
  var SHAPES    = (window.IB_SCHEMA.shapes   || []).map(function (s) { return Object.assign({}, s, { svg: SHAPE_SVGS[s.key]   || '' }); });
  var CARATS    = window.IB_SCHEMA.carats    || [];
  var CHECK_SVG = '<svg viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* Metal-aware choice cards: white-gold recolours (Platinum reuses WG). Wired 2B. */
  var IB_CARD_WG = {
    "plain": "https://cdn.shopify.com/s/files/1/1031/2718/7787/files/isla-bella-plain-band-white-gold.jpg?v=1783837079",
    "pave": "https://cdn.shopify.com/s/files/1/1031/2718/7787/files/isla-bella-pave-band-white-gold.jpg?v=1783837076",
    "solitaire": "https://cdn.shopify.com/s/files/1/1031/2718/7787/files/isla-bella-solitaire-white-gold-round.jpg?v=1783837078",
    "hidden-halo": "https://cdn.shopify.com/s/files/1/1031/2718/7787/files/isla-bella-hidden-halo-white-gold.jpg?v=1783837077",
    "halo": "https://cdn.shopify.com/s/files/1/1031/2718/7787/files/isla-bella-halo-white-gold-round.jpg?v=1783837078",
    "three-stone": "https://cdn.shopify.com/s/files/1/1031/2718/7787/files/isla-bella-trilogy-white-gold-round.jpg?v=1783837078",
  };

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 1 — DATA LAYER (REWRITTEN FOR v7.0)
     Convention-based handle list → fetch → parse → build maps → preflight
  ═══════════════════════════════════════════════════════════════════════ */

  /* Build the 64 expected handles by convention.
     This is the single source of truth for which products the builder
     expects to find in Shopify. */
  function ibBuildHandleList() {
    var out = [];
    for (var b = 0; b < BAND_KEYS.length; b++) {
      for (var s = 0; s < SETTING_KEYS.length; s++) {
        for (var sh = 0; sh < SHAPE_KEYS.length; sh++) {
          out.push('isla-bella-' + BAND_KEYS[b] + '-' + SETTING_KEYS[s] + '-' + SHAPE_KEYS[sh]);
        }
      }
    }
    return out;
  }

  var PRODUCT_HANDLES = ibBuildHandleList();   /* 64 handles */

  function ibFetchProduct(handle) {
    return fetch('/products/' + handle + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .catch(function () {
        /* One retry after 2 seconds */
        return new Promise(function (resolve, reject) {
          setTimeout(function () {
            fetch('/products/' + handle + '.js')
              .then(function (res2) {
                if (!res2.ok) throw new Error('HTTP ' + res2.status + ' (retry)');
                return res2.json();
              })
              .then(resolve)
              .catch(reject);
          }, 2000);
        });
      });
  }

  function ibNormaliseMetal(metalLabel) {
    var l = (metalLabel || '').toLowerCase();
    if (l.indexOf('platinum') !== -1) return 'pt';
    if (l.indexOf('yellow') !== -1)   return 'yg';
    if (l.indexOf('white') !== -1)    return 'wg';
    return l.replace(/\s+/g, '-');
  }

  /* Carat normalisation — "3ct", "3.0ct", "3 ct" all → "3ct" */
  function ibNormaliseCarat(label) {
    return (label || '').toLowerCase().replace(/\s+/g, '').replace(/\.0ct/, 'ct');
  }

  /* Parse a single-field carat value out of variant.option1.
     Variants in v7.0 have option1 = Carat, option2 = Metal. */
  function ibParseCarat(opt1) {
    return ibNormaliseCarat(opt1 || '');
  }

  /* Parse a product handle into its band/setting/shape components.
     Handle format: isla-bella-{band}-{setting}-{shape}
     where {setting} can be one of: solitaire, hidden-halo, halo, three-stone
     and {shape} is one of 8 single-token shapes.

     Strategy: strip the "isla-bella-" prefix, then identify the band
     (first token, must match BAND_KEYS), then match the rest against
     known {setting}-{shape} suffixes. This handles the two-token settings
     (hidden-halo, three-stone) without ambiguity because the shape list
     is fixed and never overlaps a setting suffix. */
  function ibParseHandle(handle) {
    if (!handle || handle.indexOf('isla-bella-') !== 0) return null;
    var rest = handle.substring('isla-bella-'.length);

    /* Band: first segment. Must match a known band key. */
    var bandMatch = null;
    for (var b = 0; b < BAND_KEYS.length; b++) {
      if (rest === BAND_KEYS[b] || rest.indexOf(BAND_KEYS[b] + '-') === 0) {
        bandMatch = BAND_KEYS[b];
        break;
      }
    }
    if (!bandMatch) return null;
    rest = rest.substring(bandMatch.length + 1); /* skip "{band}-" */

    /* Setting: must match one of the known setting keys (longest first
       so 'three-stone' and 'hidden-halo' beat 'halo' / 'solitaire'). */
    var settingsByLength = SETTING_KEYS.slice().sort(function (a, b) { return b.length - a.length; });
    var settingMatch = null;
    for (var s = 0; s < settingsByLength.length; s++) {
      var sk = settingsByLength[s];
      if (rest === sk + '-' + extractShape(rest, sk) ||
          rest.indexOf(sk + '-') === 0) {
        settingMatch = sk;
        break;
      }
    }
    if (!settingMatch) return null;
    rest = rest.substring(settingMatch.length + 1); /* skip "{setting}-" */

    /* Shape: whatever's left, must match a known shape key. */
    if (SHAPE_KEYS.indexOf(rest) === -1) return null;

    return { band: bandMatch, setting: settingMatch, shape: rest };
  }

  /* Helper for ibParseHandle — used inside a containment check.
     Returns the trailing shape token if it matches, or '' otherwise. */
  function extractShape(rest, settingKey) {
    var afterSetting = rest.indexOf(settingKey + '-') === 0
      ? rest.substring(settingKey.length + 1)
      : '';
    return SHAPE_KEYS.indexOf(afterSetting) !== -1 ? afterSetting : '';
  }

  function ibMapKey(bandKey, settingKey, shapeKey, caratKey, metalKey) {
    return bandKey + '|' + settingKey + '|' + shapeKey + '|' + caratKey + '|' + metalKey;
  }

  /* A6 (supplier reconciliation): LGD does not manufacture Plain Band + Halo
     in Emerald, Princess or Marquise. These three combinations must never
     reach Add to Bag — they route to the concierge for a bespoke quotation.
     Source: islabellalgdsupplierbible.xlsx (61 of 64 combinations valid). */
  var IB_CONCIERGE_ONLY = {
    'plain|halo|emerald': 1,
    'plain|halo|princess': 1,
    'plain|halo|marquise': 1
  };
  function ibIsConciergeOnly(s) {
    return !!IB_CONCIERGE_ONLY[(s.bandKey || '') + '|' + (s.settingKey || '') + '|' + (s.shapeKey || '')];
  }

  /* Live data — populated after fetch resolves.
     Map key format: "band|setting|shape|carat|metal"  (unchanged from v6.4) */
  var ibVariantMap = {};
  var ibAvailIndex = { bands: new Set() };
  var ibMinPrice   = null;
  var ibDataReady  = false;

  /* Wizard state */
  var ibWizardActive   = false;
  var ibWizardCurrent  = 0;    /* 0=Band, 1=Setting, 2=Shape, 3=Metal, 4=Carat, 5=Size */
  var ibWizardDone     = [];   /* array of completed step indices */
  var IB_WIZARD_STEPS  = ['band','metal','setting','shape','carat','size'];
  var IB_STEP_REWARDS  = [
    'A refined foundation, chosen.',
    'The silhouette is taking shape.',
    'This is starting to feel personal.',
    'The tone is set.',
    'The presence is chosen.',
    'Your ring is complete.'
  ];

  function ibBuildMaps(productResults) {
    ibVariantMap = {};
    ibAvailIndex = { bands: new Set() };
    ibMinPrice   = null;

    productResults.forEach(function (result) {
      if (!result.ok || !result.product) return;

      var product = result.product;
      var handle  = result.handle;
      var parsed  = ibParseHandle(handle);

      if (!parsed) {
        console.warn('[IB Builder] Could not parse handle "' + handle + '" — skipping product.');
        return;
      }

      if (!product.variants) return;

      product.variants.forEach(function (variant) {
        var caratKey = ibParseCarat(variant.option1);
        var metalKey = ibNormaliseMetal(variant.option2);

        if (!caratKey || !metalKey) {
          console.warn('[IB Builder] Variant in "' + handle + '" missing carat/metal — skipping.');
          return;
        }

        var key = ibMapKey(parsed.band, parsed.setting, parsed.shape, caratKey, metalKey);

        ibVariantMap[key] = {
          variantId: variant.id,
          price:     variant.price,
          sku:       variant.sku || '',
          handle:    handle,
          available: variant.available
        };

        /* Hierarchical forward-filtering availability index */
        ibAvailIndex.bands.add(parsed.band);

        var l2 = parsed.band;
        if (!ibAvailIndex[l2]) ibAvailIndex[l2] = new Set();
        ibAvailIndex[l2].add(parsed.setting);

        var l3 = parsed.band + '|' + parsed.setting;
        if (!ibAvailIndex[l3]) ibAvailIndex[l3] = new Set();
        ibAvailIndex[l3].add(parsed.shape);

        var l4 = parsed.band + '|' + parsed.setting + '|' + parsed.shape;
        if (!ibAvailIndex[l4]) ibAvailIndex[l4] = new Set();
        ibAvailIndex[l4].add(caratKey);

        var l5 = parsed.band + '|' + parsed.setting + '|' + parsed.shape + '|' + caratKey;
        if (!ibAvailIndex[l5]) ibAvailIndex[l5] = new Set();
        ibAvailIndex[l5].add(metalKey);

        if (variant.available && (ibMinPrice === null || variant.price < ibMinPrice)) {
          ibMinPrice = variant.price;
        }
      });
    });
  }

  /* Preflight console validation — developer canary, invisible to customers.
     Updated for the 64-handle / 576-variant expectation. */
  function ibPreflightCheck(fetchResults) {
    var loaded = fetchResults.filter(function (r) { return r.ok; });
    var failed = fetchResults.filter(function (r) { return !r.ok; });

    if (failed.length > 0) {
      failed.forEach(function (r) {
        console.warn('[IB Builder] PREFLIGHT: Fetch failed for product handle "' + r.handle + '". Ensure this product exists in Shopify and is Active on the Online Store channel.');
      });
    }

    /* Per-band-and-setting family completeness check */
    BAND_KEYS.forEach(function (bk) {
      SETTING_KEYS.forEach(function (sk) {
        var familyShapes = ibAvailIndex[bk + '|' + sk];
        if (!familyShapes) {
          console.warn('[IB Builder] PREFLIGHT: Family "' + bk + ' + ' + sk + '" has no live products at all.');
          return;
        }
        var missingShapes = SHAPE_KEYS.filter(function (sh) { return !familyShapes.has(sh); });
        if (missingShapes.length > 0) {
          console.warn('[IB Builder] PREFLIGHT: Family "' + bk + ' + ' + sk + '" missing shapes: ' + missingShapes.join(', '));
        }
      });
    });

    var unavailCount = Object.keys(ibVariantMap).filter(function (k) { return !ibVariantMap[k].available; }).length;
    if (unavailCount > 0) {
      console.info('[IB Builder] INFO: ' + unavailCount + ' variant(s) exist but are marked unavailable (out of stock).');
    }

    var totalExpected = BAND_KEYS.length * SETTING_KEYS.length * SHAPE_KEYS.length *
                        CARATS.length    * METAL_KEYS.length;
    console.info('[IB Builder] Ready. ' + Object.keys(ibVariantMap).length + ' / ' + totalExpected + ' valid combinations loaded from ' + loaded.length + ' / ' + PRODUCT_HANDLES.length + ' products.');
  }

  /* Public diagnostic — call ibReportMissingSKUs() in browser console.
     Updated for the 576-combination space. */
  window.ibReportMissingSKUs = function () {
    var caratKeys = CARATS.map(function (c){ return ibNormaliseCarat(c.key); });
    var missing = {};
    var totalPossible = 0, totalMissing = 0;

    BAND_KEYS.forEach(function (b) {
      SETTING_KEYS.forEach(function (se) {
        SHAPE_KEYS.forEach(function (sh) {
          caratKeys.forEach(function (c) {
            METAL_KEYS.forEach(function (m) {
              totalPossible++;
              var key = ibMapKey(b, se, sh, c, m);
              if (!ibVariantMap[key]) {
                var g = b + '-' + se;
                if (!missing[g]) missing[g] = [];
                missing[g].push(sh + '-' + c + '-' + m);
                totalMissing++;
              }
            });
          });
        });
      });
    });

    console.group('[IB Builder] Missing SKU Report');
    console.info('Total possible combinations:     ', totalPossible);
    console.info('Missing (expected but not found):', totalMissing);
    console.info('Populated:                       ', Object.keys(ibVariantMap).length);
    console.info('Min price (pence):               ', ibMinPrice);
    Object.keys(missing).sort().forEach(function (group) {
      console.log(group + ' (' + missing[group].length + ' missing):');
      missing[group].forEach(function (c) { console.log('  ' + c); });
    });
    console.groupEnd();
    return { variantMap: ibVariantMap, missing: missing };
  };

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 2 — UI STATE: loading, blocked, availability
     (Unchanged from v6.4 — included verbatim)
  ═══════════════════════════════════════════════════════════════════════ */

  /* One-time style injection (loading overlay, availability, mobile sticky
     preview). The build-mode share card was removed — sharing now lives only
     in the reveal scene (ibShareRing). */
  (function () {
    var style = document.createElement('style');
    style.textContent = [
      '.ib-steps{position:relative;}',
      '.ib-loading-overlay{position:absolute;inset:0;z-index:10;',
      'display:flex;align-items:center;justify-content:center;',
      'background:rgba(250,247,242,0.82);backdrop-filter:blur(2px);',
      '-webkit-backdrop-filter:blur(2px);',
      'transition:opacity 0.4s ease, visibility 0.4s;',
      'opacity:0;visibility:hidden;pointer-events:none;border-radius:8px;}',
      '.ib-loading-overlay.is-active{opacity:1;visibility:visible;pointer-events:auto;}',
      '.ib-loading-overlay-inner{display:flex;flex-direction:column;align-items:center;gap:14px;}',
      '.ib-loading-dots{display:flex;gap:8px;}',
      '.ib-loading-dot{width:7px;height:7px;border-radius:50%;',
      'background:rgba(184,154,106,0.5);',
      'animation:ibDotPulse 1.2s ease-in-out infinite;}',
      '.ib-loading-dot:nth-child(2){animation-delay:0.18s;}',
      '.ib-loading-dot:nth-child(3){animation-delay:0.36s;}',
      '@keyframes ibDotPulse{0%,80%,100%{transform:scale(0.6);opacity:0.3;}40%{transform:scale(1);opacity:1;}}',
      '.ib-loading-text{font-family:var(--font-display,\'Cormorant Garamond\',serif);',
      'font-size:13px;font-style:italic;font-weight:300;',
      'color:rgba(42,36,32,0.55);letter-spacing:0.04em;}',
      '.ib-loading-overlay.is-blocked .ib-loading-dots{display:none;}',
      '.ib-loading-overlay.is-blocked{background:rgba(250,247,242,0.94);}',
      '.ib-blocked-text{font-family:var(--font-display,\'Cormorant Garamond\',serif);',
      'font-size:17px;font-style:italic;font-weight:300;',
      'color:rgba(42,36,32,0.62);letter-spacing:0.02em;margin:0 0 10px;text-align:center;}',
      '.ib-blocked-small{font-family:var(--font-body,\'Jost\',sans-serif);font-size:11px;',
      'font-weight:300;color:rgba(42,36,32,0.4);letter-spacing:0.08em;text-align:center;}',
      '.ib-option.is-unavailable{opacity:0.32;}',
      '.ib-option.is-unavailable .ib-option-card{cursor:not-allowed;}',
      '.ib-option.is-unavailable .ib-option-card:hover{transform:none;border-color:var(--ib-border);',
      'box-shadow:none;background:var(--ib-warm-white);}',
      '.ib-option.is-unavailable input{pointer-events:none;}',
      '.ib-mobile-preview{display:none;}',
      '@media(max-width:1020px){',
      '.ib-mobile-preview{display:block;position:fixed;top:0;left:0;right:0;z-index:120;',
      'background:rgba(250,248,245,0.97);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);',
      'border-bottom:1px solid rgba(184,154,106,0.20);',
      'padding:14px 20px 12px;transition:opacity 0.4s ease;}',
      '.ib-mobile-preview-inner{display:flex;align-items:center;gap:18px;}',
      '.ib-mobile-preview-img{width:140px;height:140px;border-radius:12px;',
      'overflow:hidden;background:linear-gradient(148deg,#F5EFE6,#EDE5D8);flex-shrink:0;',
      'box-shadow:0 3px 12px rgba(42,36,32,0.12);}',
      '.ib-mobile-preview-img img{width:100%;height:100%;object-fit:cover;object-position:center 50%;}',
      '.ib-mobile-preview-info{flex:1;min-width:0;}',
      '.ib-mobile-preview-name{font-family:\'Cormorant Garamond\',Georgia,serif;',
      'font-size:18px;font-weight:300;font-style:italic;',
      'color:#2A2420;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;',
      'letter-spacing:0.01em;line-height:1.2;}',
      '.ib-mobile-preview-price{font-family:\'Cormorant Garamond\',Georgia,serif;',
      'font-size:17px;font-weight:300;color:#B89A6A;margin-top:6px;letter-spacing:0.01em;}',
      '}'
    ].join('');
    document.head.appendChild(style);
  })();

  function ibEnsureOverlay() {
    var steps = document.querySelector('.ib-steps');
    if (!steps) return null;
    var overlay = steps.querySelector('.ib-loading-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'ib-loading-overlay';
      overlay.innerHTML = [
        '<div class="ib-loading-overlay-inner">',
          '<div class="ib-loading-dots">',
            '<span class="ib-loading-dot"></span>',
            '<span class="ib-loading-dot"></span>',
            '<span class="ib-loading-dot"></span>',
          '</div>',
          '<div class="ib-loading-text">Preparing your atelier…</div>',
        '</div>'
      ].join('');
      steps.appendChild(overlay);
    }
    return overlay;
  }

  function ibShowLoadingState() {
    var overlay = ibEnsureOverlay();
    if (!overlay) return;
    overlay.classList.remove('is-blocked');
    overlay.classList.add('is-active');
    var inner = overlay.querySelector('.ib-loading-overlay-inner');
    if (inner) {
      inner.innerHTML = [
        '<div class="ib-loading-dots">',
          '<span class="ib-loading-dot"></span>',
          '<span class="ib-loading-dot"></span>',
          '<span class="ib-loading-dot"></span>',
        '</div>',
        '<div class="ib-loading-text">Preparing your atelier…</div>'
      ].join('');
    }
  }

  function ibHideLoadingState() {
    var overlay = document.querySelector('.ib-loading-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-active');
  }

  function ibShowBlockedState() {
    var overlay = ibEnsureOverlay();
    if (!overlay) return;
    overlay.classList.add('is-blocked');
    overlay.classList.add('is-active');
    var inner = overlay.querySelector('.ib-loading-overlay-inner');
    if (inner) {
      inner.innerHTML = [
        '<p class="ib-blocked-text">Our builder is loading</p>',
        '<span class="ib-blocked-small">Please refresh the page to try again</span>'
      ].join('');
    }
    document.querySelectorAll('.ib-cta-btn, .ib-mobile-cta:not(.ib-mobile-cta--ghost)').forEach(function (b) {
      b.disabled = true;
      b.style.opacity = '0.4';
    });
  }

  function ibSetOptionAvailability(name, key, available) {
    var input = document.querySelector('input[name="' + name + '"][data-key="' + key + '"]');
    if (!input) return;
    var label = input.closest('.ib-option');
    if (!label) return;
    if (available) {
      label.classList.remove('is-unavailable');
      input.disabled = false;
    } else {
      label.classList.add('is-unavailable');
      input.disabled = true;
      if (input.checked) input.checked = false;
    }
  }

  /* Availability filtering is informational only.
     All visible options remain selectable. If a customer picks a
     combination that has no live Shopify variant, Buy Now routes to
     concierge (CASE B — sales fallback). */
  function ibUpdateAvailability() {
    if (!ibDataReady) return;
    /* No-op by design. See CASE B in header. */
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 3 — RENDERING (UNCHANGED FROM v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  function ibRenderOptions() {
    function setColumns(mountId, count) {
      var el = document.getElementById(mountId);
      if (el) el.style.gridTemplateColumns = 'repeat(' + Math.min(count, 5) + ', 1fr)';
    }

    function ibSwatchWell(item, iconClass) {
      var videoUrl  = (typeof item.videoUrl  === 'string') ? item.videoUrl.trim()  : '';
      var swatchUrl = (typeof item.swatchUrl === 'string') ? item.swatchUrl.trim() : '';

      if (videoUrl) {
        return '<span class="ib-swatch-img"><video src="' + videoUrl + '"' +
          (swatchUrl ? ' poster="' + swatchUrl + '"' : '') +
          ' muted loop playsinline preload="none" aria-label="' + (item.label || '') + ' preview"></video></span>';
      }
      if (swatchUrl) {
        var wgUrl = (typeof IB_CARD_WG !== 'undefined' && item && IB_CARD_WG[item.key]) ? IB_CARD_WG[item.key] : '';
        var mAttr = wgUrl ? (' data-swatch-yg="' + swatchUrl + '" data-swatch-wg="' + wgUrl + '"') : '';
        return '<span class="ib-swatch-img"><img src="' + swatchUrl + '" alt="' + (item.label || '') + '" width="400" height="400" loading="lazy"' + mAttr + '></span>';
      }
      var svgMarkup = item.svg || '';
      return '<span class="' + iconClass + '">' + svgMarkup + '</span>';
    }

    var bm = document.getElementById('ib-mount-band');
    if (bm) {
      bm.innerHTML = BANDS.map(function (b) {
        return '<label class="ib-option"><input type="radio" name="ib_band" value="' + b.label + '" data-key="' + b.key + '"><span class="ib-option-card">' + ibSwatchWell(b, 'ib-setting-icon') + '<span class="ib-option-info"><span class="ib-option-name">' + b.label + '</span><span class="ib-option-sub">' + b.sub + '</span></span><span class="ib-check">' + CHECK_SVG + '</span></span></label>';
      }).join('');
    }

    var sm = document.getElementById('ib-mount-setting');
    if (sm) {
      sm.innerHTML = SETTINGS.map(function (s) {
        return '<label class="ib-option"><input type="radio" name="ib_setting" value="' + s.label + '" data-key="' + s.key + '"><span class="ib-option-card">' + ibSwatchWell(s, 'ib-setting-icon') + '<span class="ib-option-info"><span class="ib-option-name">' + s.label + '</span><span class="ib-option-sub">' + s.sub + '</span></span><span class="ib-check">' + CHECK_SVG + '</span></span></label>';
      }).join('');
      setColumns('ib-mount-setting', SETTINGS.length);
    }

    var shm = document.getElementById('ib-mount-shape');
    if (shm) {
      shm.innerHTML = SHAPES.map(function (s) {
        return '<label class="ib-option"><input type="radio" name="ib_shape" value="' + s.label + '" data-key="' + s.key + '"><span class="ib-option-card">' + ibSwatchWell(s, 'ib-shape-icon') + '<span class="ib-option-info"><span class="ib-option-name">' + s.label + '</span><span class="ib-option-sub">' + s.sub + '</span></span><span class="ib-check">' + CHECK_SVG + '</span></span></label>';
      }).join('');
      setColumns('ib-mount-shape', SHAPES.length);
    }

    var cm = document.getElementById('ib-mount-carat');
    if (cm) {
      cm.innerHTML = CARATS.map(function (c) {
        var caratSwatchUrl = (typeof c.swatchUrl === 'string') ? c.swatchUrl.trim() : '';
        var caratVisual;
        if (caratSwatchUrl) {
          caratVisual = '<span class="ib-swatch-img"><img src="' + caratSwatchUrl + '" alt="' + (c.label || '') + '" width="400" height="400" loading="lazy"></span>';
        } else {
          caratVisual = '<span class="ib-carat-icon"><svg viewBox="0 0 36 36" fill="none" style="width:' + c.svgSize + ';height:' + c.svgSize + '"><polygon points="18,3 30,13 26,33 10,33 6,13" stroke="#B89A6A" stroke-width="1.3" fill="rgba(184,154,106,0.07)"/><polygon points="18,3 24,10 18,8 12,10" stroke="#B89A6A" stroke-width="0.8"/></svg></span>';
        }
        return '<label class="ib-option"><input type="radio" name="ib_carat" value="' + c.key + '"><span class="ib-option-card">' + caratVisual + '<span class="ib-option-info"><span class="ib-option-name">' + c.label + '</span><span class="ib-option-sub">' + c.sub + '</span></span><span class="ib-check">' + CHECK_SVG + '</span></span></label>';
      }).join('');
      setColumns('ib-mount-carat', CARATS.length);
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 4 — SELECTION STATE  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  function ibSel() {
    var ba   = document.querySelector('input[name="ib_band"]:checked');
    var se   = document.querySelector('input[name="ib_setting"]:checked');
    var sh   = document.querySelector('input[name="ib_shape"]:checked');
    var me   = document.querySelector('input[name="ib_metal"]:checked');
    var ca   = document.querySelector('input[name="ib_carat"]:checked');
    var szEl = document.getElementById('ib-ring-size');
    var srEl = document.getElementById('ib-ring-sizer');
    return {
      band:               ba   ? ba.value                  : null,
      bandKey:            ba   ? ba.dataset.key            : null,
      setting:            se   ? se.value                  : null,
      settingKey:         se   ? se.dataset.key            : null,
      shape:              sh   ? sh.value                  : null,
      shapeKey:           sh   ? sh.dataset.key            : null,
      metal:              me   ? me.value                  : null,
      metalKey:           me   ? me.dataset.key            : null,
      carat:              ca   ? ca.value                  : null,
      ring_size:          szEl ? (szEl.value || '')        : '',
      ring_sizer_request: srEl ? srEl.checked              : false
    };
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 5 — PRICE & FORMATTING  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  function ibGBP(pence) {
    if (!pence && pence !== 0) return '';
    var pounds = Math.round(pence) / 100;
    return '\u00a3' + pounds.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function ibGetCurrentPrice() {
    var s = ibSel();
    if (!s.bandKey || !s.settingKey || !s.shapeKey || !s.carat || !s.metalKey) return null;
    var key   = ibMapKey(s.bandKey, s.settingKey, s.shapeKey, ibNormaliseCarat(s.carat), s.metalKey);
    var entry = ibVariantMap[key];
    return entry ? entry.price : null;
  }

  /* Selection-aware floor: cheapest available variant consistent with the
     choices made so far (any subset of band/setting/shape/carat/metal).
     Falls back to the global minimum when nothing is selected or matches. */
  function ibGetFloorPrice() {
    var s = ibSel();
    var want = [s.bandKey, s.settingKey, s.shapeKey, s.carat ? ibNormaliseCarat(s.carat) : null, s.metalKey];
    var min = null;
    Object.keys(ibVariantMap).forEach(function (k) {
      var entry = ibVariantMap[k];
      if (!entry.available) return;
      var parts = k.split('|');
      for (var i = 0; i < 5; i++) {
        if (want[i] && parts[i] !== want[i]) return;
      }
      if (min === null || entry.price < min) min = entry.price;
    });
    return min !== null ? min : ibMinPrice;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 6 — GALLERY / MEDIA  (v7.3 — replaces v6.4 Customiser-layer approach)

     Architecture:
       - Images served from assets/manifest.json (theme asset, populated via Codex URL harvest)
       - Key format: "{band}_{setting}_{shape}"  e.g. "plain_hidden-halo_oval"
       - Each entry: { sku, whitegold: { stills:[], frames:[], frames_count:0 }, yellowgold:{...} }
       - Concierge entries: { "concierge": true }
       - Placeholder entries: stills:[] + frames:[] → shows hero default until manifest is populated
       - If stills empty but frames exist, builder uses frames[0] as hero still

     Viewer priority for any selection:
       1. Manifest stills (gallery — multi-image with thumbnail strip)
       2. Customiser preview_video_* layers (video, existing system — kept as fallback)
       3. Hero default (ib-layer-default — shown until manifest is populated)

     360 viewer:
       - Canvas-based, drag to spin
       - Activated by ib-360-btn inside ib-image-frame
       - Requires manifest.frames (full URL array) with length > 0
  ═══════════════════════════════════════════════════════════════════════ */

  /* ── Manifest state ───────────────────────────────────────────────── */
  var _ibManifest         = null;      /* loaded JSON object */
  var _ibManifestReady    = false;

  /* ── Gallery state ────────────────────────────────────────────────── */
  var _ibGalleryMainSrc   = '';        /* URL of the image currently in the gallery viewer */
  var _ibGalleryMainAlt   = '';
  var _ibGalleryStills    = [];        /* stills array for the active combination */
  var _ibGalleryIndex     = 0;

  /* ── 360 state ────────────────────────────────────────────────────── */
  var _ib360Active        = false;
  var _ib360Frames        = [];        /* preloaded Image objects, indexed 0..N-1 */
  var _ib360FrameUrls     = [];        /* raw URL array from manifest.frames */
  var _ib360FrameIndex    = 0;
  var _ib360FramesTotal   = 0;
  var _ib360DragBound     = false;
  var _ib360HintHidden    = false;
  var _ib360Pushed        = false;     /* true while a history entry is held open for the 360 viewer */
  var _ib360LoadedKey     = '';        /* identity of the frame set currently loaded in the canvas */

  /* ── Legacy layer state (kept for video-layer fallback) ───────────── */
  var _activeLayer        = null;
  var _isDefaultLayer     = true;

  /* ────────────────────────────────────────────────────────────────────
     ibFetchManifest — fetches /assets/manifest.json once at boot.
     Runs in parallel with the 64-product handle fetch.
     Fails gracefully: builder works with hero-default until populated.
  ──────────────────────────────────────────────────────────────────── */
  function ibFetchManifest() {
    fetch((typeof window !== 'undefined' && window.IB_MANIFEST_URL) || '/assets/manifest.json')
      .then(function (r) {
        if (!r.ok) throw new Error('[IB Builder] manifest.json returned ' + r.status);
        return r.json();
      })
      .then(function (data) {
        _ibManifest      = data;
        _ibManifestReady = true;
        console.info('[IB Builder] manifest.json loaded.');
        /* If the builder is already active and a selection has been made,
           re-run ibUpdate so gallery stills appear without user interaction. */
        if (ibDataReady) ibUpdate();
      })
      .catch(function (err) {
        console.warn('[IB Builder] manifest.json unavailable — gallery inactive. Hero default will show for all selections.', err);
        _ibManifest      = {};
        _ibManifestReady = true;
      });
  }

  /* Return the manifest entry for a combination key, or null. */
  function ibManifestEntry(bandKey, settingKey, shapeKey) {
    if (!_ibManifest) return null;
    return _ibManifest[bandKey + '_' + settingKey + '_' + shapeKey] || null;
  }

  /* Return the best image URL available for the current state.
     Used by lightbox, mobile preview, and cart builder-image property. */
  function ibGetCurrentImageUrl() {
    if (_ibGalleryMainSrc) return _ibGalleryMainSrc;
    if (_activeLayer) {
      var layerImg = _activeLayer.querySelector('img');
      if (layerImg && layerImg.src && layerImg.src.indexOf('blank.gif') === -1) return layerImg.src;
    }
    return '';
  }

  /* ────────────────────────────────────────────────────────────────────
     ibSwitchGallery — main viewer switch, called from ibUpdate().
     Replaces ibSwitchMedia from v6.4/7.1.
  ──────────────────────────────────────────────────────────────────── */
  function ibSwitchGallery(bandKey, settingKey, shapeKey, metalKey) {
    /* No complete selection yet → hero default */
    if (!bandKey || !settingKey || !shapeKey) {
      ibGalleryShowDefault();
      return;
    }

    /* ── Priority 1: manifest stills ── */
    if (_ibManifestReady) {
      var entry = ibManifestEntry(bandKey, settingKey, shapeKey);
      if (entry) {
        if (entry.concierge) {
          /* Combination routes to concierge; show default.
             The cart / Buy Now path already handles the concierge redirect. */
          ibGalleryShowDefault();
          ibMetalTint(metalKey);
          return;
        }
        /* Pick metal data: YG for yellow gold, WG/PT both use whitegold images */
        var metalData = (metalKey === 'yg' && entry.yellowgold) ? entry.yellowgold : entry.whitegold;
        /* stills: if empty but frames exist, use first frame as hero still */
        var mStills = (metalData && metalData.stills) ? metalData.stills : [];
        var mFrames = (metalData && metalData.frames) ? metalData.frames : [];
        var effectiveStills = mStills.length > 0 ? mStills : (mFrames.length > 0 ? [mFrames[0]] : []);
        if (metalData && effectiveStills.length > 0) {
          ibGalleryLoadStills(effectiveStills, settingKey, shapeKey, metalKey);
          /* Offer 360 button when frames array is available */
          ibGallery360Arm(mFrames.length > 0, mFrames);
          return;
        }
      }
    }

    /* ── Priority 2: Customiser preview_video_* layer ── */
    var videoTarget = ibGetMedia(bandKey, settingKey, shapeKey, metalKey);
    if (videoTarget && videoTarget !== document.getElementById('ib-layer-default')) {
      ibGalleryHidePanel();
      ibGallery360Arm(false, []);
      ibGalleryHideThumbs();
      _ibGalleryMainSrc = '';
      if (_activeLayer && _activeLayer !== videoTarget) {
        _activeLayer.classList.remove('is-active');
        ibPauseLayerVideo(_activeLayer);
      }
      videoTarget.classList.add('is-active');
      ibPlayLayerVideo(videoTarget);
      _activeLayer    = videoTarget;
      _isDefaultLayer = false;
      ibMetalTint(metalKey);
      ibUpdateMobilePreview();
      return;
    }

    /* ── Priority 3: hero default ── */
    ibGalleryShowDefault();
    ibMetalTint(metalKey);
  }

  /* ────────────────────────────────────────────────────────────────────
     Gallery panel helpers
  ──────────────────────────────────────────────────────────────────── */

  /* Load a stills array into the gallery viewer */
  function ibGalleryLoadStills(stills, settingKey, shapeKey, metalKey) {
    _ibGalleryStills = stills;
    _ibGalleryIndex  = 0;
    var altBase = [settingKey.replace(/-/g, ' '), shapeKey, 'engagement ring'].join(' ');

    ibGalleryShowPanel();
    ibGallerySetMainImg(stills[0], altBase);
    ibGalleryRenderThumbs(stills, altBase);
    ibMetalTint(metalKey);
    ibUpdateMobilePreview();
  }

  /* Set the main gallery image (fades out old, fades in new) */
  function ibGallerySetMainImg(src, alt) {
    var mainImg = document.getElementById('ib-gallery-main-img');
    if (!mainImg || !src) return;
    _ibGalleryMainSrc = src;
    _ibGalleryMainAlt = alt || '';
    mainImg.alt = _ibGalleryMainAlt;
    var spinner = document.getElementById('ib-gallery-spinner');

    /* Skip animation if src unchanged */
    if (mainImg.src === src) return;

    var tmp = new Image();
    tmp.onload = function () {
      /* Fade out */
      mainImg.classList.add('is-fading');
      setTimeout(function () {
        mainImg.src = src;
        /* Fade in on next paint */
        requestAnimationFrame(function () {
          mainImg.classList.remove('is-fading');
          if (spinner) spinner.style.display = 'none';
        });
      }, 180);
    };
    tmp.onerror = function () {
      mainImg.src = src;
      if (spinner) spinner.style.display = 'none';
    };
    if (spinner) spinner.style.display = '';
    tmp.src = src;
  }

  /* Build and show the thumbnail strip */
  function ibGalleryRenderThumbs(stills, altBase) {
    var strip = document.getElementById('ib-thumb-strip');
    if (!strip) return;
    if (!stills || stills.length <= 1) {
      strip.style.display = 'none';
      strip.innerHTML = '';
      return;
    }
    strip.innerHTML = '';
    stills.forEach(function (src, i) {
      var btn = document.createElement('button');
      btn.className = 'ib-thumb-btn' + (i === 0 ? ' is-active' : '');
      btn.type = 'button';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.setAttribute('aria-label', 'View image ' + (i + 1) + ' of ' + stills.length);
      var img = document.createElement('img');
      img.src     = src;
      img.alt     = '';
      img.width   = 56;
      img.height  = 56;
      img.loading = i === 0 ? 'eager' : 'lazy';
      btn.appendChild(img);
      /* Closure to capture index */
      (function (idx) {
        btn.addEventListener('click', function () {
          ibGalleryThumbClick(idx, altBase + ' view ' + (idx + 1));
        });
      }(i));
      strip.appendChild(btn);
    });
    strip.style.display = '';
  }

  /* Thumbnail click — switch main image, update active state */
  function ibGalleryThumbClick(index, alt) {
    _ibGalleryIndex = index;
    if (_ibGalleryStills[index]) ibGallerySetMainImg(_ibGalleryStills[index], alt);
    /* Sync thumb button states */
    var btns = document.querySelectorAll('#ib-thumb-strip .ib-thumb-btn');
    btns.forEach(function (btn, i) {
      btn.classList.toggle('is-active', i === index);
      btn.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    /* Exit 360 if it was active — return to still view */
    if (_ib360Active) ibExit360();
  }

  /* Show gallery panel; hide default layer and any active video layer */
  function ibGalleryShowPanel() {
    var gallery = document.getElementById('ib-gallery-viewer');
    var def     = document.getElementById('ib-layer-default');
    if (gallery) { gallery.style.display = ''; gallery.setAttribute('aria-hidden', 'false'); }
    if (def) def.classList.remove('is-active');
    if (_activeLayer && _activeLayer !== def) {
      _activeLayer.classList.remove('is-active');
      ibPauseLayerVideo(_activeLayer);
    }
    _activeLayer    = null;
    _isDefaultLayer = false;
  }

  /* Hide gallery panel */
  function ibGalleryHidePanel() {
    var gallery = document.getElementById('ib-gallery-viewer');
    if (gallery) { gallery.style.display = 'none'; gallery.setAttribute('aria-hidden', 'true'); }
  }

  /* Show hero default; clear all gallery / 360 state */
  function ibGalleryShowDefault() {
    ibGalleryHidePanel();
    ibGallery360Arm(false, []);
    ibGalleryHideThumbs();
    _ibGalleryMainSrc = '';
    _ibGalleryMainAlt = '';
    _ibGalleryStills  = [];
    _ibGalleryIndex   = 0;
    var def = document.getElementById('ib-layer-default');
    if (def) {
      def.classList.add('is-active');
      ibPlayLayerVideo(def);
    }
    if (_activeLayer && _activeLayer !== def) {
      _activeLayer.classList.remove('is-active');
      ibPauseLayerVideo(_activeLayer);
    }
    _activeLayer    = def;
    _isDefaultLayer = true;
    ibMetalTint(null);
    ibUpdateMobilePreview();
  }

  function ibGalleryHideThumbs() {
    var strip = document.getElementById('ib-thumb-strip');
    if (strip) strip.style.display = 'none';
  }

  /* Arm / disarm the 360 toggle button with frame data */
  function ibGallery360Arm(show, frames) {
    var btn = document.getElementById('ib-360-btn');
    if (!btn) return;
    if (!show || !frames || !frames.length) {
      btn.style.display = 'none';
      if (_ib360Active) ibExit360();   /* new design can't spin → drop back to the still */
      _ib360FrameUrls = [];
      _ib360LoadedKey = '';
      return;
    }
    var changed = frames.join('|') !== _ib360LoadedKey;
    _ib360FrameUrls = frames;
    btn.style.display = '';
    /* If the customer is already spinning and the design changed, morph the live
       360 to the new design automatically (no need to tap a thumbnail first). */
    if (_ib360Active && changed) ib360Reload(frames);
  }

  /* ────────────────────────────────────────────────────────────────────
     Legacy layer system — kept intact as Priority-2 fallback for
     Customiser preview_video_* layers.
  ──────────────────────────────────────────────────────────────────── */

  function ibSetDefaultPreview() {
    var target = document.getElementById('ib-layer-default');
    if (target) {
      target.classList.add('is-active');
      _activeLayer    = target;
      _isDefaultLayer = true;
      ibPlayLayerVideo(target);
    }
    ibUpdateMobilePreview();
  }

  function ibMediaReady(layerEl) {
    if (!layerEl) return false;
    var img = layerEl.querySelector('img');
    if (img) { var iSrc = (img.getAttribute('src') || '').trim(); if (iSrc && iSrc !== '/' && iSrc.indexOf('blank.gif') === -1) return true; }
    var vid = layerEl.querySelector('video');
    if (vid) { var vSrc = (vid.getAttribute('src') || '').trim(); if (vSrc && vSrc !== '/') return true; }
    return false;
  }

  function ibPlayLayerVideo(layerEl) {
    if (!layerEl) return;
    var vid = layerEl.querySelector('video');
    if (!vid) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (vid.readyState === 0) vid.load();
    vid.play().catch(function () {});
  }

  function ibPauseLayerVideo(layerEl) {
    if (!layerEl) return;
    var vid = layerEl.querySelector('video');
    if (vid && !vid.paused) { vid.pause(); vid.currentTime = 0; }
  }

  /* Strict layer resolver — used only for Priority-2 video-layer fallback.
     Unchanged from v7.1. Never cross-band/cross-setting/cross-shape. */
  function ibGetMedia(bandKey, settingKey, shapeKey, metalKey) {
    if (!bandKey || !settingKey || !shapeKey) {
      return document.getElementById('ib-layer-default');
    }
    var exactBase = '.ib-image-layer'
      + '[data-band="'    + bandKey    + '"]'
      + '[data-setting="' + settingKey + '"]'
      + '[data-shape="'   + shapeKey   + '"]';
    if (metalKey) {
      var t1Vid = document.querySelector(exactBase + '[data-metal="' + metalKey + '"][data-media="video"]');
      if (t1Vid && ibMediaReady(t1Vid)) return t1Vid;
      var t1Img = document.querySelector(exactBase + '[data-metal="' + metalKey + '"]:not([data-media])');
      if (t1Img && ibMediaReady(t1Img)) return t1Img;
    }
    var t2Vid = document.querySelector(exactBase + ':not([data-metal])[data-media="video"]');
    if (t2Vid && ibMediaReady(t2Vid)) return t2Vid;
    var t2Img = document.querySelector(exactBase + ':not([data-metal]):not([data-media])');
    if (t2Img && ibMediaReady(t2Img)) return t2Img;
    return document.getElementById('ib-layer-default');
  }

  function ibMetalTint(metalKey) {
    var tYg = document.getElementById('ib-tint-yg');
    var tPt = document.getElementById('ib-tint-pt');
    if (_isDefaultLayer) {
      if (tYg) tYg.classList.remove('is-active');
      if (tPt) tPt.classList.remove('is-active');
      return;
    }
    if (tYg) tYg.classList.toggle('is-active', metalKey === 'yg');
    if (tPt) tPt.classList.toggle('is-active', metalKey === 'pt');
  }

  /* ────────────────────────────────────────────────────────────────────
     360 SPIN VIEWER
     - Activated by ibToggle360() (called from ib-360-btn onclick)
     - Canvas-based; drag / swipe to rotate
     - Frame URLs served from manifest.frames (full array, not a template string)
     - Falls back cleanly if frames fail to load
  ──────────────────────────────────────────────────────────────────── */

  window.ibToggle360 = function () {
    if (_ib360Active) {
      ibExit360();
      /* Consume the history entry we added on open, so Back stays clean. */
      if (_ib360Pushed) { _ib360Pushed = false; try { window.history.back(); } catch (e) {} }
    } else {
      if (!_ib360FrameUrls.length) return;
      ibEnter360(_ib360FrameUrls);
    }
  };

  /* Load (or hot-swap) a frame set into the canvas.
     silent=true keeps the current frame on screen until the first new frame is
     ready — used when the design changes mid-spin, so there's no flash. */
  function ib360LoadFrames(frameUrls, silent) {
    /* Cap very large spin sets so loading stays fast. Per-frame quality is
       unchanged — we just sample fewer angles (72 frames = 5 deg steps). */
    var MAX_SPIN = 72;
    var urls = frameUrls;
    if (frameUrls.length > MAX_SPIN) {
      urls = [];
      for (var k = 0; k < MAX_SPIN; k++) { urls.push(frameUrls[Math.floor(k * frameUrls.length / MAX_SPIN)]); }
    }
    var framesCount = urls.length;
    _ib360FramesTotal = framesCount;
    _ib360FrameIndex  = 0;
    _ib360Frames      = new Array(framesCount);
    _ib360LoadedKey   = frameUrls.join('|');

    var loading = document.getElementById('ib-360-loading');
    if (loading) loading.style.display = silent ? 'none' : '';

    /* Load order: frame 0, then an even spread across the whole rotation, then
       the rest. With the nearest-loaded fallback in ib360DrawFrame the customer
       can start spinning almost immediately (coarse first, then it sharpens). */
    var order = [], seenIdx = {};
    function pushIdx(n){ n = ((n % framesCount) + framesCount) % framesCount; if (!seenIdx[n]) { seenIdx[n] = 1; order.push(n); } }
    pushIdx(0);
    var spread = Math.min(12, framesCount);
    for (var s = 0; s < spread; s++) { pushIdx(Math.round(s * framesCount / spread)); }
    for (var f = 0; f < framesCount; f++) { pushIdx(f); }

    var firstDrawn = false;
    order.forEach(function (frameIdx) {
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () {
        _ib360Frames[frameIdx] = img;
        if (!firstDrawn) { firstDrawn = true; if (loading) loading.style.display = 'none'; ib360BindDrag(); }
        ib360DrawFrame(_ib360FrameIndex);
      };
      img.onerror = function () { if (!firstDrawn && loading) loading.style.display = 'none'; };
      img.src = urls[frameIdx];
    });
  }

  function ibEnter360(frameUrls) {
    _ib360Active     = true;
    _ib360HintHidden = false;

    /* Push a history entry so the browser Back button closes the 360 viewer
       and returns to the still image (mirrors the zoom lightbox). */
    if (!_ib360Pushed) { try { window.history.pushState({ ib360: 1 }, ''); _ib360Pushed = true; } catch (e) {} }

    var wrap    = document.getElementById('ib-360-canvas-wrap');
    var hint    = document.getElementById('ib-360-hint');
    var btn     = document.getElementById('ib-360-btn');
    var gallery = document.getElementById('ib-gallery-viewer');
    if (gallery) gallery.style.display = 'none';
    if (wrap)    wrap.style.display = '';
    if (hint)    hint.classList.remove('is-hidden');
    if (btn)     btn.setAttribute('aria-pressed', 'true');

    ib360LoadFrames(frameUrls, false);
  }

  /* Selection changed while the spinner is open: morph the live 360 to the new
     design's frames without leaving the viewer or touching history. */
  function ib360Reload(frameUrls) {
    var hint = document.getElementById('ib-360-hint');
    if (hint) hint.classList.add('is-hidden');
    ib360LoadFrames(frameUrls, true);
  }

  function ibExit360() {
    _ib360Active = false;
    var wrap    = document.getElementById('ib-360-canvas-wrap');
    var btn     = document.getElementById('ib-360-btn');
    var gallery = document.getElementById('ib-gallery-viewer');
    if (wrap) wrap.style.display = 'none';
    if (btn)  btn.setAttribute('aria-pressed', 'false');
    ib360UnbindDrag();
    /* Restore gallery still if one was showing */
    if (_ibGalleryMainSrc && gallery) gallery.style.display = '';
  }

  function ib360NearestLoaded(idx) {
    /* Find the closest already-loaded frame to idx, scanning outward both ways. */
    for (var d = 1; d <= _ib360FramesTotal; d++) {
      var a = ((idx - d) % _ib360FramesTotal + _ib360FramesTotal) % _ib360FramesTotal;
      if (_ib360Frames[a]) return _ib360Frames[a];
      var b = (idx + d) % _ib360FramesTotal;
      if (_ib360Frames[b]) return _ib360Frames[b];
    }
    return null;
  }

  function ib360DrawFrame(idx) {
    var canvas = document.getElementById('ib-360-canvas');
    if (!canvas) return;
    var frame = _ib360Frames[idx] || ib360NearestLoaded(idx);
    if (!frame) return;
    var dpr = window.devicePixelRatio || 1;
    var w   = canvas.offsetWidth  || canvas.parentElement.offsetWidth;
    var h   = canvas.offsetHeight || canvas.parentElement.offsetHeight;
    if (!w || !h) return;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width  = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(frame, 0, 0, w, h);
    ctx.restore();
  }

  function ib360BindDrag() {
    if (_ib360DragBound) return;
    var canvas = document.getElementById('ib-360-canvas');
    if (!canvas) return;
    _ib360DragBound = true;

    var isDragging  = false;
    var startX      = 0;
    var sensitivity = 4;  /* pixels per frame step */

    function moveBy(delta) {
      if (!delta) return;
      var steps = Math.round(delta / sensitivity);
      if (!steps) return;
      _ib360FrameIndex = ((_ib360FrameIndex - steps) % _ib360FramesTotal + _ib360FramesTotal) % _ib360FramesTotal;
      ib360DrawFrame(_ib360FrameIndex);
      /* Hide hint after first interaction */
      if (!_ib360HintHidden) {
        var hint = document.getElementById('ib-360-hint');
        if (hint) hint.classList.add('is-hidden');
        _ib360HintHidden = true;
      }
    }

    function onMouseDown(e) { isDragging = true; startX = e.clientX; canvas.style.cursor = 'grabbing'; e.preventDefault(); }
    function onMouseMove(e) {
      if (!isDragging) return;
      var delta = e.clientX - startX;
      moveBy(delta);
      startX = e.clientX;
    }
    function onMouseUp() { isDragging = false; canvas.style.cursor = 'grab'; }

    var touchStartX = 0;
    function onTouchStart(e) { touchStartX = e.touches[0].clientX; }
    function onTouchMove(e) {
      var delta = e.touches[0].clientX - touchStartX;
      moveBy(delta);
      touchStartX = e.touches[0].clientX;
      e.preventDefault();
    }

    canvas.addEventListener('mousedown',   onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);
    canvas.addEventListener('touchstart',  onTouchStart, { passive: true });
    canvas.addEventListener('touchmove',   onTouchMove,  { passive: false });

    canvas._ib360L = { onMouseDown: onMouseDown, onMouseMove: onMouseMove, onMouseUp: onMouseUp, onTouchStart: onTouchStart, onTouchMove: onTouchMove };
  }

  function ib360UnbindDrag() {
    var canvas = document.getElementById('ib-360-canvas');
    if (!canvas || !canvas._ib360L) return;
    var L = canvas._ib360L;
    canvas.removeEventListener('mousedown',   L.onMouseDown);
    document.removeEventListener('mousemove', L.onMouseMove);
    document.removeEventListener('mouseup',   L.onMouseUp);
    canvas.removeEventListener('touchstart',  L.onTouchStart);
    canvas.removeEventListener('touchmove',   L.onTouchMove);
    canvas._ib360L   = null;
    _ib360DragBound  = false;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 7 — MOBILE PREVIEW  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  function ibUpdateMobilePreview() {
    var mpImg   = document.getElementById('ib-mp-img');
    var mpName  = document.getElementById('ib-mp-name');
    var mpPrice = document.getElementById('ib-mp-price');
    if (!mpImg) return;
    var _mImgSrc = ibGetCurrentImageUrl();
    if (_mImgSrc) { mpImg.src = _mImgSrc; mpImg.style.display = 'block'; }
    var s = ibSel();
    if (mpName) {
      var parts = [s.setting, s.shape].filter(Boolean);
      mpName.textContent = parts.length ? parts.join(' \u00b7 ') : 'Isla Bella';
    }
    if (mpPrice) {
      var price = ibGetCurrentPrice();
      if (price !== null) {
        mpPrice.textContent = ibGBP(price);
      } else if (ibMinPrice !== null) {
        mpPrice.textContent = 'From ' + ibGBP(ibMinPrice);
      } else {
        mpPrice.textContent = '';
      }
    }
  }

  function ibInsertMobilePreview() {
    var builder = document.getElementById('ib-ring-builder');
    if (!builder) return;
    var mp = document.createElement('div');
    mp.className = 'ib-mobile-preview';
    mp.id = 'ib-mobile-preview';
    mp.innerHTML = [
      '<div class="ib-mobile-preview-inner">',
        '<div class="ib-mobile-preview-img">',
          '<img id="ib-mp-img" src="" alt="Your ring" width="140" height="140">',
        '</div>',
        '<div class="ib-mobile-preview-info">',
          '<div class="ib-mobile-preview-name" id="ib-mp-name">Isla Bella</div>',
          '<div class="ib-mobile-preview-price" id="ib-mp-price"></div>',
        '</div>',
      '</div>'
    ].join('');
    document.body.appendChild(mp);
  }

  var _ibPreviewTopLocked = false;

  function ibPositionMobilePreview() {
    if (window.innerWidth > 1020) return;
    var preview = document.getElementById('ib-mobile-preview');
    if (!preview) return;
    var candidateSelectors = ['header','[role="banner"]','#shopify-section-header','#shopify-section-announcement-bar','.shopify-section-header','.announcement-bar','.header-wrapper','.site-header','.sticky-header'];
    var maxBottom = 0;
    var foundSomething = false;
    candidateSelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.offsetParent === null && window.getComputedStyle(el).position !== 'fixed') return;
        var pos = window.getComputedStyle(el).position;
        if (pos !== 'fixed' && pos !== 'sticky') return;
        var rect = el.getBoundingClientRect();
        if (rect.top > 80 || rect.height < 1) return;
        if (rect.bottom > maxBottom) { maxBottom = rect.bottom; foundSomething = true; }
      });
    });
    var offset = foundSomething ? Math.min(Math.max(72, Math.round(maxBottom) + 18), 160) : 72;
    preview.style.top = offset + 'px';
    _ibPreviewTopLocked = true;
  }

  function ibUpdateMobilePreviewFade() {
    if (window.innerWidth > 1020) return;
    var preview = document.getElementById('ib-mobile-preview');
    var bar = document.getElementById('ib-mobile-bar');
    if (!preview || !bar) return;
    var gap = bar.getBoundingClientRect().top - preview.getBoundingClientRect().bottom;
    var opacity = gap >= 160 ? 1 : gap <= 40 ? 0 : (gap - 40) / 120;
    preview.style.opacity = opacity;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 8 — MAIN UPDATE  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  function ibSetRow(id, val) {
    var el = document.getElementById(id);
    if (!el) return;
    el.textContent = val || '\u2014';
    el.classList.toggle('is-placeholder', !val);
  }

  function ibUpdateCardMetal(metalKey) {
    var useWg = (metalKey === 'wg' || metalKey === 'pt');
    document.querySelectorAll('.ib-swatch-img img[data-swatch-wg]').forEach(function (img) {
      var want = useWg ? img.getAttribute('data-swatch-wg') : img.getAttribute('data-swatch-yg');
      if (want && img.getAttribute('src') !== want) img.setAttribute('src', want);
    });
  }

  function ibUpdate() {
    var s = ibSel();
    ibUpdateCardMetal(s.metalKey);

    ibSetRow('ib-sum-band',    s.band);
    ibSetRow('ib-sum-setting', s.setting);
    ibSetRow('ib-sum-shape',   s.shape);
    ibSetRow('ib-sum-metal',   s.metal);
    ibSetRow('ib-sum-carat',   s.carat);
    ibSetRow('ib-sum-size',    s.ring_size || null);

    var parts = [s.setting, s.band, s.shape, s.carat, s.metal].filter(Boolean);
    var titleEl  = document.getElementById('ib-sum-title');
    var eyebrow  = document.querySelector('.ib-summary-eyebrow');
    var summaryEl = document.querySelector('.ib-summary');

    /* All five core choices made → reveal state */
    var isComplete = !!(s.bandKey && s.settingKey && s.shapeKey && s.metalKey && s.carat);
    if (summaryEl) summaryEl.classList.toggle('is-complete', isComplete);

    if (titleEl) {
      if (isComplete && !ibWizardActive) {
        /* Curated reveal: shape + setting as the ring's name */
        titleEl.textContent = (s.shape || '') + ' ' + (s.setting || '');
      } else if (!isComplete) {
        titleEl.textContent = parts.length
          ? parts.join(' \u00b7 ')
          : ((window.IB_SCHEMA.copy && window.IB_SCHEMA.copy.summaryIdle) || 'Begin your selection');
      }
      /* When isComplete && ibWizardActive: leave the title for ibWizardReveal() to set. */
    }
    /* In wizard mode the eyebrow/title reveal is owned by ibWizardReveal()/ibWizardEdit().
       Price and gallery still update normally above/below \u2014 only this text is skipped. */
    if (!ibWizardActive) {
      if (eyebrow && isComplete) {
        eyebrow.textContent = 'Your Ring';
      } else if (eyebrow) {
        eyebrow.textContent = (window.IB_SCHEMA.copy && window.IB_SCHEMA.copy.summaryEyebrow)
          || eyebrow.dataset.ibOrig
          || eyebrow.textContent;
        if (!eyebrow.dataset.ibOrig) eyebrow.dataset.ibOrig = eyebrow.textContent;
      }
    }

    var capEl    = document.getElementById('ib-img-caption');
    var capParts = [s.setting, s.band, s.shape].filter(Boolean);
    if (capEl) capEl.textContent = capParts.length ? capParts.join(' \u00b7 ') : ((window.IB_SCHEMA.copy && window.IB_SCHEMA.copy.captionIdle) || 'Select a style to begin');

    ibSwitchGallery(s.bandKey, s.settingKey, s.shapeKey, s.metalKey);

    var elPrice  = document.getElementById('ib-price-display');
    var elVat    = document.getElementById('ib-vat-note');
    var elMobile = document.getElementById('ib-mobile-price');

    var currentPrice = ibGetCurrentPrice();

    /* Price update with fade */
    function ibSetPrice(el, text, removeClass, addClass) {
      if (!el) return;
      el.classList.add('is-updating');
      setTimeout(function () {
        el.textContent = text;
        el.classList.remove('is-updating', 'is-empty');
        if (removeClass) el.classList.remove(removeClass);
        if (addClass)    el.classList.add(addClass);
      }, 140);
    }

    if (currentPrice !== null) {
      var fmt = ibGBP(currentPrice);
      ibSetPrice(elPrice, fmt);
      if (elVat)    { elVat.style.display = 'block'; }
      if (elMobile) { elMobile.textContent = fmt; }
      window._ibPrice = fmt;
    } else {
      var _cp = window.IB_SCHEMA.copy || {};
      var fromStr = (_cp.priceIdle || 'Starting from') + ' ' + (ibMinPrice !== null ? ibGBP(ibMinPrice) : '');
      ibSetPrice(elPrice, fromStr, null, 'is-empty');
      if (elVat)    { elVat.style.display = 'none'; }
      if (elMobile) { var _floor = ibGetFloorPrice(); elMobile.textContent = (_cp.priceFrom || 'From') + ' ' + (_floor !== null ? ibGBP(_floor) : ''); }
      window._ibPrice = null;
    }

    ibUpdateAvailability();
    ibPersistSelection();
    ibUpdateMobilePreview();
    ibPushDesignUrl();  /* Silent URL update — encodes current design into query string */

    /* Show / hide "Save This Ring" button in summary */
    var saveBtn = document.getElementById('ib-save-btn');
    if (saveBtn) saveBtn.style.display = isComplete ? '' : 'none';
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 9 — ADD TO CART  (unchanged from v6.4)
     Ring size is sent as a line-item PROPERTY, never as a variant.
  ═══════════════════════════════════════════════════════════════════════ */

  window.ibAddToCart = function () {
    var s = ibSel();

    /* A ring size is required UNLESS the customer requested the complimentary
       ring sizer — in that case the order proceeds with the sizer flag attached
       and we size the ring after the sizer comes back. */
    if (!s.ring_size && !s.ring_sizer_request) {
      var szEl = document.getElementById('ib-ring-size');
      if (szEl) { szEl.style.borderColor = '#C0392B'; szEl.focus(); setTimeout(function () { szEl.style.borderColor = ''; }, 3000); }
      var sizeStep = document.getElementById('ib-step-size') || szEl;
      if (sizeStep) sizeStep.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      /* M-08: same accessible inline pattern as the wizard and the PDPs */
      ibShowStepError(document.querySelector('.ib-size-step') || (sizeStep && sizeStep.parentNode),
        'Please select your ring size — or request the complimentary sizer — before adding to bag.');
      return;
    }

    if (!s.bandKey || !s.settingKey || !s.shapeKey || !s.carat || !s.metalKey) {
      var firstStep = document.querySelector('.ib-step');
      if (firstStep) firstStep.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      return;
    }

    /* A6: concierge-only combinations are never basketed, even though the
       Shopify products exist — the supplier cannot make them. */
    if (ibIsConciergeOnly(s)) {
      console.info('[IB Builder] Concierge-only combination — routing to bespoke quotation:', s.bandKey, s.settingKey, s.shapeKey);
      if (typeof window.ibGoToConcierge === 'function') window.ibGoToConcierge();
      return;
    }

    var key   = ibMapKey(s.bandKey, s.settingKey, s.shapeKey, ibNormaliseCarat(s.carat), s.metalKey);
    var entry = ibVariantMap[key];

    if (!entry) {
      console.info('[IB Builder] Selection not currently built in Shopify — routing to concierge:', key);
      if (typeof window.ibGoToConcierge === 'function') window.ibGoToConcierge();
      return;
    }

    if (!entry.available) {
      console.info('[IB Builder] Variant exists but is currently unavailable — routing to concierge:', key);
      if (typeof window.ibGoToConcierge === 'function') window.ibGoToConcierge();
      return;
    }

    var builderImageUrl = ibGetCurrentImageUrl();

    /* Metal and Carat are the Shopify variant's OWN options
       (option1 = Carat, option2 = Metal), so the cart and checkout
       already display them as the variant line. We deliberately do NOT
       repeat them as line-item properties here — that repetition was
       the cause of "Metal" and "Carat" appearing twice in the cart.
       Band, Setting and Shape are NOT variant options (they live in the
       product handle), so they ARE sent as properties to keep the cart
       line fully explicit. Carat + Metal remain fully captured via the
       variant title and the _SKU Ref below. */
    var properties = {
      'Band':      s.band      || '',
      'Setting':   s.setting   || '',
      'Shape':     s.shape     || '',
      'Ring Size': s.ring_size || 'To be confirmed (sizer requested)',
      '_SKU Ref':  entry.sku   || ''
    };
    /* Complimentary ring sizer: travels with the order so fulfilment knows to
       post a sizer before the ring is made. */
    if (s.ring_sizer_request) properties['Free Sizer Requested'] = 'Yes';
    if (builderImageUrl) properties['_builder_image'] = builderImageUrl;

    var btns = document.querySelectorAll('.ib-cta-btn, .ib-mobile-cta:not(.ib-mobile-cta--ghost)');
    btns.forEach(function (b) { b.disabled = true; b.style.opacity = '0.6'; });

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id: entry.variantId, quantity: 1, properties: properties })
    })
    .then(function (res) {
      if (!res.ok) return res.json().then(function (e) { throw e; });
      return res.json();
    })
    .then(function () {
      window.location.href = '/cart';
    })
    .catch(function (err) {
      btns.forEach(function (b) { b.disabled = false; b.style.opacity = ''; });
      console.error('[IB Builder] Cart add failed:', err);
      ibPersistSelection();
    });
  };

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 10 — SHARE FEATURE  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  function ibBuildShareText() {
    var s = ibSel();
    var parts = [];
    if (s.setting) parts.push(s.setting);
    if (s.band)    parts.push(s.band);
    if (s.shape)   parts.push(s.shape + ' diamond');
    if (s.metal)   parts.push(s.metal);
    if (s.carat)   parts.push(s.carat);
    var selText = parts.length ? parts.join(' \u00b7 ') : 'A beautiful diamond engagement ring';
    var price   = window._ibPrice ? ' \u2014 ' + window._ibPrice : '';
    return 'I found this ring at Isla Bella Diamonds: ' + selText + price + '\n' + ibGetDesignUrl();
  }

  function ibOpenShareMore(text, url) {
    var encoded     = encodeURIComponent(url);
    var encodedText = encodeURIComponent(text);
    var links = [
      { label: 'Facebook',           href: 'https://www.facebook.com/sharer/sharer.php?u=' + encoded },
      { label: 'X / Twitter',        href: 'https://twitter.com/intent/tweet?text=' + encodedText },
      { label: 'Pinterest',          href: 'https://pinterest.com/pin/create/button/?url=' + encoded + '&description=' + encodeURIComponent('My ring design at Isla Bella Diamonds') },
      { label: 'Facebook Messenger', href: 'fb-messenger://share?link=' + encoded }
    ];
    var existing = document.getElementById('ib-share-more-modal');
    if (existing) existing.remove();
    var modal = document.createElement('div');
    modal.id = 'ib-share-more-modal';
    modal.style.cssText = 'position:fixed;inset:0;z-index:450;display:flex;align-items:center;justify-content:center;background:rgba(28,25,23,0.75);backdrop-filter:blur(8px);padding:24px;';
    var inner = document.createElement('div');
    inner.style.cssText = 'background:#1C1917;border-radius:10px;padding:32px 28px;max-width:340px;width:100%;border:1px solid rgba(184,154,106,0.25);position:relative;';
    var closeBtn = document.createElement('button');
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = 'position:absolute;top:14px;right:18px;background:none;border:none;color:rgba(255,255,255,0.4);font-size:20px;cursor:pointer;';
    closeBtn.addEventListener('click', function () { modal.remove(); });
    var title = document.createElement('p');
    title.textContent = 'Share This Ring';
    title.style.cssText = 'font-family:\'Cormorant Garamond\',serif;font-size:20px;font-weight:300;font-style:italic;color:#F5F3EF;margin:0 0 20px;letter-spacing:0.02em;';
    inner.appendChild(closeBtn);
    inner.appendChild(title);
    links.forEach(function (l) {
      var a = document.createElement('a');
      a.href = l.href; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.textContent = l.label;
      a.style.cssText = 'display:block;padding:11px 16px;margin-bottom:8px;border:1px solid rgba(255,255,255,0.1);border-radius:3px;color:rgba(250,247,242,0.6);text-decoration:none;font-family:\'Jost\',sans-serif;font-size:12px;font-weight:300;letter-spacing:0.08em;transition:border-color 0.25s,color 0.25s;';
      a.addEventListener('mouseenter', function () { a.style.borderColor = 'rgba(184,154,106,0.4)'; a.style.color = 'rgba(250,247,242,0.9)'; });
      a.addEventListener('mouseleave', function () { a.style.borderColor = 'rgba(255,255,255,0.1)'; a.style.color = 'rgba(250,247,242,0.6)'; });
      inner.appendChild(a);
    });
    var igNote = document.createElement('p');
    igNote.textContent = 'For Instagram: copy the link above and paste into your story or DM.';
    igNote.style.cssText = 'font-family:\'Jost\',sans-serif;font-size:10px;color:rgba(250,247,242,0.28);letter-spacing:0.05em;line-height:1.6;margin-top:14px;';
    inner.appendChild(igNote);
    modal.appendChild(inner);
    document.body.appendChild(modal);
    modal.addEventListener('click', function (e) { if (e.target === modal) modal.remove(); });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 10B — SAVE & SHARE DESIGN STATE  (v7.4)
     URL-based design restore. Ring size intentionally excluded — personal
     preference, not shareable.
     Format: ?design={band}_{settingcode}_{shape}_{carat}_{metalkey}
     e.g.    ?design=plain_hiddenhalo_oval_3ct_wg
  ═══════════════════════════════════════════════════════════════════════ */

  /* Setting key compact codes (hyphens stripped) */
  var SETTING_COMPACT = {
    'solitaire':   'solitaire',
    'hidden-halo': 'hiddenhalo',
    'halo':        'halo',
    'three-stone': 'threestone'
  };
  var SETTING_EXPAND = {
    'solitaire': 'solitaire',
    'hiddenhalo': 'hidden-halo',
    'halo': 'halo',
    'threestone': 'three-stone'
  };

  function ibEncodeDesign(s) {
    if (!s.bandKey || !s.settingKey || !s.shapeKey || !s.metalKey || !s.carat) return null;
    var settingCode = SETTING_COMPACT[s.settingKey] || s.settingKey.replace(/-/g, '');
    return [s.bandKey, settingCode, s.shapeKey, s.carat.toLowerCase(), s.metalKey].join('_');
  }

  function ibDecodeDesign(str) {
    if (!str || typeof str !== 'string') return null;
    var parts = str.toLowerCase().split('_');
    if (parts.length !== 5) return null;
    var bandKey    = parts[0];
    var settingKey = SETTING_EXPAND[parts[1]];
    var shapeKey   = parts[2];
    var carat      = parts[3];
    var metalKey   = parts[4];
    if (BAND_KEYS.indexOf(bandKey)   === -1) return null;
    if (!settingKey)                          return null;
    if (SHAPE_KEYS.indexOf(shapeKey) === -1) return null;
    if (['2ct','3ct','4ct'].indexOf(carat)   === -1) return null;
    if (['wg','yg','pt'].indexOf(metalKey)   === -1) return null;
    return { bandKey: bandKey, settingKey: settingKey, shapeKey: shapeKey, carat: carat, metalKey: metalKey };
  }

  function ibGetDesignUrl() {
    var s = ibSel();
    var code = ibEncodeDesign(s);
    if (!code) return window.location.href;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('design', code);
      return url.toString();
    } catch (e) {
      return window.location.href + (window.location.search ? '&' : '?') + 'design=' + code;
    }
  }

  /* Update the browser URL bar silently — no reload, no history entry */
  function ibPushDesignUrl() {
    try {
      var url = ibGetDesignUrl();
      if (url !== window.location.href) {
        window.history.replaceState(null, '', url);
      }
    } catch (e) {}
  }

  /* B3: set true when a valid ?design= restored all five selections, so the
     wizard can jump the recipient of a shared link to the Size step (the only
     thing the design code doesn't carry) instead of stranding them on step 1. */
  var ibDesignRestoredFromUrl = false;

  /* Restore design from URL after the UI option cards exist in the DOM */
  function ibRestoreDesignFromUrl() {
    try {
      var params = new URLSearchParams(window.location.search);
      var code = params.get('design');
      if (!code) return;
      var design = ibDecodeDesign(code);
      if (!design) return;  /* Invalid — ignore gracefully */

      function pick(name, matchFn) {
        document.querySelectorAll('[name="' + name + '"]').forEach(function (inp) {
          if (matchFn(inp)) {
            inp.checked = true;
            inp.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
      pick('ib_band',    function (i) { return i.dataset.key === design.bandKey;    });
      pick('ib_setting', function (i) { return i.dataset.key === design.settingKey; });
      pick('ib_shape',   function (i) { return i.dataset.key === design.shapeKey;   });
      pick('ib_metal',   function (i) { return i.dataset.key === design.metalKey;   });
      pick('ib_carat',   function (i) { return i.value       === design.carat;      });
      var _rs = ibSel();
      if (_rs.bandKey && _rs.settingKey && _rs.shapeKey && _rs.metalKey && _rs.carat) {
        ibDesignRestoredFromUrl = true;
      }
    } catch (e) {
      /* Never surface errors from URL restore */
    }
  }

  /* B3: after ibWizardInit resets the wizard to step 0, if a shared design was
     restored, mark the five design steps complete and open the Size step so the
     recipient sees the restored ring and only has to choose their size. */
  function ibWizardApplyRestoredDesign() {
    if (!ibDesignRestoredFromUrl) return;
    var s = ibSel();
    if (!(s.bandKey && s.settingKey && s.shapeKey && s.metalKey && s.carat)) return;
    ibWizardDone = [0, 1, 2, 3, 4];   /* band, metal, setting, shape, carat */
    ibWizardShowStep(5);              /* Size — scrolls the shared design into view */
  }

  /* Save design: copy the design URL to clipboard, show quiet confirmation */
  window.ibSaveDesign = function () {
    var url = ibGetDesignUrl();
    var confirmEl = document.getElementById('ib-save-confirm');
    function showConfirm() {
      if (!confirmEl) return;
      confirmEl.style.display = 'block';
      confirmEl.classList.add('is-visible');
      setTimeout(function () {
        confirmEl.classList.remove('is-visible');
        setTimeout(function () { confirmEl.style.display = 'none'; }, 300);
      }, 2800);
    }
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(showConfirm).catch(function () {
        /* Fallback to execCommand */
        var ta = document.createElement('textarea');
        ta.value = url; ta.style.cssText = 'position:fixed;opacity:0;';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
        showConfirm();
      });
    } else {
      var ta = document.createElement('textarea');
      ta.value = url; ta.style.cssText = 'position:fixed;opacity:0;';
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
      showConfirm();
    }
  };

  /* Share My Ring (reveal action) — native share sheet where available,
     otherwise the share-more modal (Facebook / X / Pinterest / copy link). */
  window.ibShareRing = function () {
    var text = ibBuildShareText();
    var url  = ibGetDesignUrl();
    if (navigator.share) {
      navigator.share({ title: 'My Ring — Isla Bella Diamonds', text: text, url: url }).catch(function () {});
    } else {
      ibOpenShareMore(text, url);
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 11 — SWATCH VIDEO  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  function ibPlaySwatchVideo(card) {
    if (!card) return;
    var vid = card.querySelector('.ib-swatch-img video');
    if (!vid) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (vid.readyState === 0) vid.load();
    vid.play().catch(function () {});
  }
  function ibPauseSwatchVideo(card) {
    if (!card) return;
    var vid = card.querySelector('.ib-swatch-img video');
    if (vid && !vid.paused) { vid.pause(); vid.currentTime = 0; }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 12 — PERSISTENCE & STATE HELPERS  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  function ibPersistSelection() {
    var s = ibSel();
    try {
      sessionStorage.setItem('ib_selection', JSON.stringify({
        band: s.band || '',
        setting: s.setting || '', shape: s.shape || '', metal: s.metal || '',
        carat: s.carat || '', price: window._ibPrice || '',
        ring_size: s.ring_size || '', ring_sizer_request: s.ring_sizer_request || false
      }));
    } catch (e) {}
  }

  window.ibGetState = function () {
    var s = ibSel();
    return {
      band: s.band || '', bandKey: s.bandKey || '',
      setting: s.setting || '', settingKey: s.settingKey || '',
      shape: s.shape || '', shapeKey: s.shapeKey || '',
      metal: s.metal || '', metalKey: s.metalKey || '',
      carat: s.carat || '', price: window._ibPrice || '',
      ring_size: s.ring_size || '', ring_sizer_request: s.ring_sizer_request || false
    };
  };

  window.ibBuildCartProperties = function () {
    var state = window.ibGetState();
    var props = {};
    if (state.band)               props['Band']                 = state.band;
    if (state.setting)            props['Setting']              = state.setting;
    if (state.shape)              props['Diamond Shape']        = state.shape;
    if (state.metal)              props['Metal']                = state.metal;
    if (state.carat)              props['Carat Weight']         = state.carat;
    if (state.ring_size)          props['Ring Size']            = state.ring_size;
    if (state.ring_sizer_request) props['Free Sizer Requested'] = 'Yes';
    return props;
  };

  var _ibLbPushed = false;
  window.ibOpenLightbox = function () {
    if (_ib360Active) return;            /* don't open the zoom while the 360 spinner is active */
    var lb      = document.getElementById('ib-lightbox');
    var content = document.getElementById('ib-lightbox-content');
    if (!lb || !content) return;
    if (lb.classList.contains('is-open')) return;
    var _lbSrc = ibGetCurrentImageUrl();
    var _lbAlt = _ibGalleryMainAlt || (function() { var ai = document.querySelector('.ib-image-layer.is-active img'); return ai ? ai.alt : ''; }());
    content.innerHTML = _lbSrc
      ? '<img src="' + _lbSrc + '" alt="' + (_lbAlt || 'Isla Bella ring') + '" width="900" height="900">'
      : '<div class="ib-lightbox-placeholder"></div>';
    lb.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    /* Push a history entry so the browser Back button closes the zoom
       rather than navigating away from the builder. */
    try { window.history.pushState({ ibLb: 1 }, ''); _ibLbPushed = true; } catch (e) {}
  };
  window.ibCloseLightbox = function () {
    var lb = document.getElementById('ib-lightbox');
    var wasOpen = lb && lb.classList.contains('is-open');
    if (lb) lb.classList.remove('is-open');
    document.body.style.overflow = '';
    /* Consume the history entry we added on open, so history stays clean. */
    if (wasOpen && _ibLbPushed) { _ibLbPushed = false; try { window.history.back(); } catch (e) {} }
  };
  /* Browser Back closes an open zoom instead of leaving the page. */
  window.addEventListener('popstate', function () {
    var lb = document.getElementById('ib-lightbox');
    if (lb && lb.classList.contains('is-open')) {
      _ibLbPushed = false;
      lb.classList.remove('is-open');
      document.body.style.overflow = '';
      return;
    }
    /* Back while the 360 spinner is open closes it and returns to the still image. */
    if (_ib360Active) {
      _ib360Pushed = false;
      ibExit360();
    }
  });

  window.ibToggleDQ = function (e) {
    e.stopPropagation();
    var popup   = document.getElementById('ib-dq-popup');
    var trigger = document.getElementById('ib-dq-trigger');
    var scrim   = document.getElementById('ib-dq-scrim');
    if (!popup) return;
    var isOpen = popup.classList.contains('is-open');
    if (isOpen) { ibCloseDQ(); }
    else { popup.classList.add('is-open'); if (scrim) scrim.classList.add('is-open'); if (trigger) trigger.setAttribute('aria-expanded', 'true'); }
  };
  window.ibCloseDQ = function () {
    var popup   = document.getElementById('ib-dq-popup');
    var trigger = document.getElementById('ib-dq-trigger');
    var scrim   = document.getElementById('ib-dq-scrim');
    if (popup)   popup.classList.remove('is-open');
    if (scrim)   scrim.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  };

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 13 — CONCIERGE OVERLAY  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  window.ibGoToConcierge = window.ibOpenConcierge = function () {
    ibPersistSelection();
    var state = window.ibGetState();
    function cqSet(id, val) { var el = document.getElementById(id); if (el && val) el.value = val; }
    cqSet('ib-cq-h-setting',   state.setting);
    cqSet('ib-cq-h-shape',     state.shape);
    cqSet('ib-cq-h-metal',     state.metal);
    cqSet('ib-cq-h-carat',     state.carat);
    cqSet('ib-cq-h-price',     state.price);
    cqSet('ib-cq-h-ring-size', state.ring_size);
    var cqSzSel = document.getElementById('ib-cq-ring-size-select');
    if (cqSzSel && state.ring_size) cqSzSel.value = state.ring_size;
    if (state.ring_sizer_request) cqSet('ib-cq-h-sizer-req', 'Yes');
    var parts  = [state.carat, state.shape, state.setting, state.band, state.metal].filter(Boolean);
    var sumLine = document.getElementById('ib-cq-sum-line');
    if (sumLine) sumLine.textContent = parts.length ? parts.join(' \u00b7 ') : 'No selection yet \u2014 tell us what you have in mind below.';
    var sumRows = document.getElementById('ib-cq-sum-rows');
    if (sumRows) {
      var rowData = [['Band', state.band],['Setting', state.setting],['Shape', state.shape],['Metal', state.metal],['Carat', state.carat],['Ring Size', state.ring_size]];
      var rowsHTML = rowData.filter(function (r) { return !!r[1]; }).map(function (r) { return '<span class="ib-cq-sum-row">' + r[0] + ': <strong>' + r[1] + '</strong></span>'; }).join('');
      var priceEl = document.getElementById('ib-cq-sum-price');
      if (priceEl) priceEl.textContent = state.price || '';
      sumRows.insertAdjacentHTML('afterbegin', rowsHTML);
    }
    var sizerAlert = document.getElementById('ib-cq-sizer-alert');
    if (sizerAlert && state.ring_sizer_request) sizerAlert.classList.add('is-visible');
    var panel       = document.getElementById('ib-concierge-panel');
    var sizerBanner = document.getElementById('ib-cq-sizer-banner');
    if (state.ring_sizer_request) {
      if (panel)       panel.classList.add('is-sizer-mode');
      if (sizerBanner) sizerBanner.style.display = 'block';
    } else {
      if (panel)       panel.classList.remove('is-sizer-mode');
      if (sizerBanner) sizerBanner.style.display = 'none';
    }
    var formEl    = document.getElementById('ib-cq-form');
    var successEl = document.getElementById('ib-cq-success');
    if (formEl)    formEl.style.display = '';
    if (successEl) successEl.classList.remove('is-visible');
    var overlay = document.getElementById('ib-concierge-overlay');
    if (overlay) {
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      setTimeout(function () { var first = overlay.querySelector('input[type="text"]'); if (first) first.focus(); }, 420);
    }
  };

  window.ibCloseConcierge = function () {
    var overlay = document.getElementById('ib-concierge-overlay');
    if (overlay) { overlay.classList.remove('is-open'); document.body.style.overflow = ''; }
    var cqSzSel2 = document.getElementById('ib-cq-ring-size-select');
    if (cqSzSel2) cqSzSel2.value = '';
    var sumRows = document.getElementById('ib-cq-sum-rows');
    if (sumRows) {
      var priceEl = document.getElementById('ib-cq-sum-price');
      sumRows.innerHTML = '';
      if (priceEl) { priceEl.textContent = ''; sumRows.appendChild(priceEl); }
    }
    var sizerAlert  = document.getElementById('ib-cq-sizer-alert');
    var panel2      = document.getElementById('ib-concierge-panel');
    var sizerBanner = document.getElementById('ib-cq-sizer-banner');
    var addrEl      = document.getElementById('ib-cq-address');
    if (sizerAlert)  sizerAlert.classList.remove('is-visible');
    if (panel2)      panel2.classList.remove('is-sizer-mode');
    if (sizerBanner) sizerBanner.style.display = 'none';
    if (addrEl)      { addrEl.value = ''; addrEl.style.borderColor = ''; }
  };

  (function () {
    var overlay = document.getElementById('ib-concierge-overlay');
    if (overlay) overlay.addEventListener('click', function (e) { if (e.target === overlay) ibCloseConcierge(); });
  })();

  window.ibSubmitConcierge = function () {
    var name    = (document.getElementById('ib-cq-name')    || {}).value || '';
    var email   = (document.getElementById('ib-cq-email')   || {}).value || '';
    var address = (document.getElementById('ib-cq-address') || {}).value || '';
    var state   = window.ibGetState();
    if (!name.trim() || !email.trim()) {
      var firstEmpty = !name.trim() ? document.getElementById('ib-cq-name') : document.getElementById('ib-cq-email');
      if (firstEmpty) { firstEmpty.style.borderColor = '#C0392B'; firstEmpty.focus(); setTimeout(function () { firstEmpty.style.borderColor = ''; }, 2500); }
      return;
    }
    if (state.ring_sizer_request && !address.trim()) {
      var addrEl = document.getElementById('ib-cq-address');
      if (addrEl) { addrEl.style.borderColor = '#C0392B'; addrEl.focus(); addrEl.placeholder = 'Please enter your delivery address for the ring sizer'; setTimeout(function () { addrEl.style.borderColor = ''; addrEl.placeholder = 'Street, City, Postcode'; }, 3000); }
      return;
    }
    var cqSzOverride = (document.getElementById('ib-cq-ring-size-select') || {}).value || '';
    if (cqSzOverride) state.ring_size = cqSzOverride;
    var prefEl   = document.querySelector('#ib-cq-pref input:checked');
    var msg      = (document.getElementById('ib-cq-message')  || {}).value || '';
    var timeline = (document.getElementById('ib-cq-timeline') || {}).value || '';
    var budget   = (document.getElementById('ib-cq-budget')   || {}).value || '';
    var lines = ['=== ISLA BELLA CONCIERGE ENQUIRY ===', ''];
    if (state.ring_sizer_request) { lines.push('!!! FREE RING SIZER REQUESTED !!!'); lines.push('Post sizer to customer BEFORE production begins.'); lines.push('---'); lines.push(''); }
    if (state.band || state.setting || state.shape || state.metal || state.carat) {
      lines.push('RING SELECTION'); lines.push('---');
      if (state.carat)     lines.push('Carat:     ' + state.carat);
      if (state.shape)     lines.push('Shape:     ' + state.shape);
      if (state.band)      lines.push('Band:      ' + state.band);
      if (state.setting)   lines.push('Setting:   ' + state.setting);
      if (state.metal)     lines.push('Metal:     ' + state.metal);
      if (state.ring_size) lines.push('Ring Size: ' + state.ring_size);
      lines.push('Diamond:   D-F / VVS / Excellent / IGI');
      if (state.price) lines.push('Price:     ' + state.price + ' (inc. 20% UK VAT)');
      lines.push('');
    }
    if (msg.trim()) { lines.push('Customer message:'); lines.push(msg.trim()); lines.push(''); }
    lines.push('---');
    if (prefEl && prefEl.value) lines.push('Contact via: ' + prefEl.value);
    if (timeline) lines.push('Timeline:    ' + timeline);
    if (budget)   lines.push('Budget:      ' + budget);
    lines.push(''); lines.push('CUSTOMER DETAILS');
    lines.push('Name:    ' + name); lines.push('Email:   ' + email);
    if (address.trim()) lines.push('Address: ' + address.trim());
    var bodyField = document.getElementById('ib-cq-h-body');
    if (bodyField) bodyField.value = lines.join('\n');
    var btn = document.getElementById('ib-cq-submit-btn');
    if (btn) { btn.disabled = true; btn.style.opacity = '0.6'; }
    /* hCaptcha blocks AJAX POSTs to /contact from this modal, so hand off to
       the fixed /pages/concierge: persist the selection + the customer's
       details, then redirect there to complete the (hCaptcha-protected) send. */
    try {
      sessionStorage.setItem('ib_selection', JSON.stringify({
        band: state.band || '', setting: state.setting || '', shape: state.shape || '',
        metal: state.metal || '', carat: state.carat || '', ring_size: state.ring_size || '',
        ring_sizer_request: state.ring_sizer_request || false, price: state.price || ''
      }));
      sessionStorage.setItem('ib_cq_prefill', JSON.stringify({
        name: name, email: email, address: address,
        contact_preference: (prefEl ? prefEl.value : ''),
        message: msg, timeline: timeline, budget: budget
      }));
    } catch (e) {}
    window.location.href = '/pages/concierge';
  };

  function ibShowConciergeSuccess() {
    var formEl    = document.getElementById('ib-cq-form');
    var successEl = document.getElementById('ib-cq-success');
    if (formEl)    formEl.style.display = 'none';
    if (successEl) successEl.classList.add('is-visible');
    var panel = document.getElementById('ib-concierge-panel');
    if (panel) panel.scrollTop = 0;
  }

  (function () {
    var pref = document.getElementById('ib-cq-pref');
    if (!pref) return;
    pref.addEventListener('change', function (e) {
      var hidden = document.getElementById('ib-cq-pref-val');
      if (hidden && e.target.type === 'radio') hidden.value = e.target.value;
    });
  })();

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 14 — LEAD CAPTURE MODAL  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  window.ibOpenModal = function () {
    var s = ibSel();
    function ibSetModal(id, val) { var el = document.getElementById(id); if (el) el.textContent = val || '\u2014'; }
    ibSetModal('ib-mod-band',    s.band);
    ibSetModal('ib-mod-setting', s.setting);
    ibSetModal('ib-mod-shape',   s.shape);
    ibSetModal('ib-mod-metal',   s.metal);
    ibSetModal('ib-mod-carat',   s.carat);
    ibSetModal('ib-mod-price',   window._ibPrice || '\u2014');
    ibPersistSelection();
    var mf = document.getElementById('ib-modal-form');
    var ms = document.getElementById('ib-modal-success');
    var mo = document.getElementById('ib-modal');
    if (mf) mf.style.display = '';
    if (ms) ms.classList.remove('is-visible');
    if (mo) { mo.classList.add('is-open'); document.body.style.overflow = 'hidden'; }
  };
  window.ibCloseModal = function () {
    var mo = document.getElementById('ib-modal');
    if (mo) { mo.classList.remove('is-open'); document.body.style.overflow = ''; }
  };
  (function () {
    var mo = document.getElementById('ib-modal');
    if (mo) mo.addEventListener('click', function (e) { if (e.target === mo) ibCloseModal(); });
  })();

  window.ibSubmitForm = function () {
    var name  = (document.getElementById('ib-name')  || {}).value || '';
    var email = (document.getElementById('ib-email') || {}).value || '';
    if (!name.trim() || !email.trim()) { alert('Please enter your name and email address to continue.'); return; }
    var s = ibSel();
    var lines = [
      '=== ISLA BELLA BESPOKE ENQUIRY ===', '',
      'Band:      ' + (s.band      || 'Not selected'),
      'Setting:   ' + (s.setting   || 'Not selected'),
      'Shape:     ' + (s.shape     || 'Not selected'),
      'Metal:     ' + (s.metal     || 'Not selected'),
      'Carat:     ' + (s.carat     || 'Not selected'),
      'Ring Size: ' + (s.ring_size || 'Not selected'),
      'Diamond:   D-F / VVS / Excellent / IGI',
      'Price:     ' + (window._ibPrice || 'Awaiting full selection') + ' (inc. 20% UK VAT)',
      '', '--- Customer ---', 'Name:  ' + name, 'Email: ' + email
    ];
    var submitBtn = document.querySelector('.ib-submit-btn');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.style.opacity = '0.6'; }
    /* hCaptcha blocks AJAX /contact, so hand off to the fixed concierge page:
       persist the selection + name/email, then redirect there to complete. */
    try {
      sessionStorage.setItem('ib_selection', JSON.stringify({
        band: s.band || '', setting: s.setting || '', shape: s.shape || '',
        metal: s.metal || '', carat: s.carat || '', ring_size: s.ring_size || '',
        ring_sizer_request: s.ring_sizer_request || false, price: window._ibPrice || ''
      }));
      sessionStorage.setItem('ib_cq_prefill', JSON.stringify({ name: name, email: email }));
    } catch (e) {}
    window.location.href = '/pages/concierge';
  };

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 15 — GLOBAL EVENT LISTENERS  (unchanged from v6.4)
  ═══════════════════════════════════════════════════════════════════════ */

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { window.ibCloseDQ && ibCloseDQ(); ibCloseLightbox(); ibCloseModal(); ibCloseConcierge(); }
  });
  document.addEventListener('click', function (e) {
    var trigger = document.getElementById('ib-dq-trigger');
    if (trigger && !trigger.contains(e.target)) ibCloseDQ();
  });
  (function () {
    var trigger = document.getElementById('ib-dq-trigger');
    if (!trigger) return;
    var closeTimer;
    trigger.addEventListener('mouseenter', function () {
      clearTimeout(closeTimer);
      var popup = document.getElementById('ib-dq-popup');
      if (popup) popup.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
    });
    trigger.addEventListener('mouseleave', function () { closeTimer = setTimeout(ibCloseDQ, 220); });
  })();

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 16B — WIZARD MODE
     One step at a time guided reveal. All underlying product/cart/manifest
     logic is completely untouched. This only controls visibility of steps.
  ═══════════════════════════════════════════════════════════════════════ */

  /* Home slot of the ring gallery (.ib-viewer) so it returns to the exact
     position in the right preview panel when leaving the reveal scene. */
  var _ibViewerHomeParent = null;
  var _ibViewerHomeNext   = null;

  /* Gentle shake to signal "make a choice first" — no error text. */
  function ibShake(el) {
    if (!el) return;
    el.style.animation = 'none';
    void el.offsetHeight; /* force reflow so the animation re-triggers */
    el.style.animation = 'ib-shake 0.4s ease';
  }

  /* M-08: blocked actions must explain themselves visibly AND accessibly —
     one pattern shared with the PDP ring-size guard (role=alert inline error
     that clears as soon as the customer makes a choice). */
  function ibShowStepError(hostEl, msg) {
    if (!hostEl) return;
    var err = hostEl.querySelector('.ib-step-error');
    if (!err) {
      err = document.createElement('p');
      err.className = 'ib-step-error';
      err.setAttribute('role', 'alert');
      err.style.cssText = 'margin:0.8rem 0 0;font-size:1.3rem;font-weight:500;color:#B3261E;line-height:1.5;';
      hostEl.appendChild(err);
    }
    err.textContent = msg;
    err.style.display = 'block';
  }
  function ibClearStepErrors() {
    document.querySelectorAll('.ib-step-error').forEach(function (e) { e.style.display = 'none'; });
  }
  /* Any new choice clears every step error (delegated once, survives re-renders) */
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.matches && t.matches('input[name^="ib_"], #ib-ring-size, #ib-ring-sizer')) ibClearStepErrors();
  }, true);

  /* Bottom edge (px from viewport top) of whatever is pinned to the top of the
     screen — the theme's sticky nav + announcement bar. Detected by GEOMETRY
     rather than computed position, because the actual pinned element on this
     theme is a position:sticky section group whose children read as static.
     A height cap excludes the tall (non-pinned) hero header. */
  function ibStickyHeaderBottom() {
    var sels = ['header', 'sticky-header', '.header-wrapper',
                '.shopify-section-group-header-group',
                '#shopify-section-header', '.shopify-section-header',
                '#shopify-section-announcement-bar', '.announcement-bar',
                '.ib-header-announce', '.site-header', '.sticky-header'];
    var maxBottom = 0;
    var seen = [];
    sels.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (seen.indexOf(el) !== -1) return;
        seen.push(el);
        var r = el.getBoundingClientRect();
        /* pinned near the top, sensible bar height (10–220px) */
        if (r.top > 80 || r.bottom <= 0 || r.height < 10 || r.height > 220) return;
        if (r.bottom > maxBottom) maxBottom = r.bottom;
      });
    });
    return maxBottom;
  }

  /* ONE consistent scroll target for every wizard transition.
     Desktop: bring the builder grid to the top.
     Mobile: land on the ACTIVE STEP so the customer always sees, in order,
     the step number, title, divider, explanation and the top of the cards —
     just below the sticky header with a little breathing space. The mobile
     grid is display:contents (no box), so we must target the step itself. */
  function ibWizardScrollToActive() {
    var active = document.querySelector('.ib-step--active');
    if (window.innerWidth > 1020 || !active) {
      var grid = document.getElementById('ib-builder-grid');
      if (grid) grid.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      return;
    }
    var offset = ibStickyHeaderBottom() + 24; /* breathing space */
    var y = active.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, y), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  function ibWizardInit() {
    var builder = document.querySelector('.ib-builder');
    if (!builder) return;
    builder.classList.add('ib-wizard');
    ibWizardActive  = true;
    ibWizardCurrent = 0;
    ibWizardDone    = [];
    /* Remember where the ring gallery lives so we can move it to the left
       inspection host on reveal and bring it back here on Edit. */
    var viewer = document.querySelector('.ib-viewer');
    if (viewer && !_ibViewerHomeParent) {
      _ibViewerHomeParent = viewer.parentNode;
      _ibViewerHomeNext   = viewer.nextSibling;
    }
    ibWizardShowStep(0, true);  /* no auto-scroll on boot — keep the hero in view */
    ibWizardUpdateProgress();
    /* Hide feature box on wizard init */
    var fb = document.getElementById('ib-feature-box');
    if (fb) fb.style.display = 'none';
    /* Hide mobile bar Add to Bag until reveal */
    var mobileCta = document.querySelector('.ib-mobile-cta:not(.ib-mobile-cta--ghost)');
    if (mobileCta) mobileCta.style.display = 'none';
  }

  function ibWizardShowStep(n, noScroll) {
    /* Any step navigation while the final reveal is open must drop back to
       preview mode (Back button, completed progress circle, or Continue). */
    if (document.querySelector('.ib-summary-wrap.is-reveal')) ibWizardExitReveal();
    var steps = document.querySelectorAll('.ib-step');
    steps.forEach(function (el, i) {
      el.classList.toggle('ib-step--active', i === n);
    });
    ibWizardCurrent = n;
    ibWizardUpdateProgress();
    /* One standardised scroll anchor for every step change (Continue & Back).
       Skipped on the initial boot so the page does not yank past the hero. */
    if (!noScroll) ibWizardScrollToActive();
  }

  function ibWizardUpdateProgress() {
    var btns = document.querySelectorAll('.ib-progress-step');
    btns.forEach(function (btn, i) {
      btn.classList.remove('is-active');
      if (i === ibWizardCurrent) btn.classList.add('is-active');
      if (ibWizardDone.indexOf(i) > -1) {
        btn.setAttribute('data-completed', '');
        btn.disabled = false;
      } else {
        btn.removeAttribute('data-completed');
        btn.disabled = (i > ibWizardCurrent);
      }
    });
    var counter = document.getElementById('ib-progress-counter');
    if (counter) counter.textContent = 'Step ' + (ibWizardCurrent + 1) + ' of 6';
  }

  /* Progress circle click — go back to a completed step */
  function ibWizardProgressClick(n) {
    if (ibWizardDone.indexOf(n) === -1 && n !== ibWizardCurrent) return;
    ibWizardShowStep(n);
  }

  window.ibWizardContinue = function (n) {
    /* Validate a choice was made for the current step before advancing */
    var stepNames = ['ib_band', 'ib_metal', 'ib_setting', 'ib_shape', 'ib_carat'];
    if (n < 5) {
      var inputName = stepNames[n];
      if (inputName) {
        var checked = document.querySelector('[name="' + inputName + '"]:checked');
        if (!checked) {
          /* No selection — shake, then say why we stayed (M-08). */
          var activeStep = document.querySelector('.ib-step--active');
          ibShake(activeStep ? (activeStep.querySelector('.ib-options') || activeStep) : null);
          ibShowStepError(activeStep, 'Please make a selection to continue.');
          return;
        }
      }
    }

    /* Mark current step as done */
    if (ibWizardDone.indexOf(n) === -1) ibWizardDone.push(n);

    /* Show emotional reward briefly */
    ibWizardShowReward(n);

    /* Advance to next step */
    var next = n + 1;
    if (next < 6) {
      setTimeout(function () { ibWizardShowStep(next); }, 600);
    }
  };

  window.ibWizardBack = function (n) {
    var prev = n - 1;
    if (prev >= 0) ibWizardShowStep(prev);
  };

  window.ibWizardComplete = function () {
    /* Validate size or sizer is set */
    var sizeVal  = document.getElementById('ib-ring-size');
    var sizerVal = document.getElementById('ib-ring-sizer');
    var hasSizeChoice = (sizeVal && sizeVal.value) || (sizerVal && sizerVal.checked);
    if (!hasSizeChoice) {
      /* No size and no sizer — shake, then say why we stayed (M-08). */
      var sizeStep = document.querySelector('.ib-size-step');
      ibShake(sizeStep ? (sizeStep.querySelector('.ib-size-wrap') || sizeStep) : null);
      ibShowStepError(sizeStep || document.querySelector('.ib-step--active'),
        'Please select your ring size — or request the complimentary sizer — to continue.');
      if (sizeVal) sizeVal.focus();
      return;
    }
    if (ibWizardDone.indexOf(5) === -1) ibWizardDone.push(5);
    ibWizardShowReward(5);
    setTimeout(function () { ibWizardReveal(); }, 800);
  };

  window.ibWizardEdit = function () {
    /* Leave the reveal and return to the first step. Selections are preserved. */
    ibWizardExitReveal();
    ibWizardShowStep(0);
  };

  /* Exit final reveal → preview mode. Hides the buy panel immediately and
     restores the preview heading/subcopy. Called by ibWizardEdit and by any
     step navigation (Back / completed progress circle / Continue). */
  function ibWizardExitReveal() {
    var wrap   = document.querySelector('.ib-summary-wrap');
    var buy    = document.getElementById('ib-buy-panel');
    var head   = document.getElementById('ib-preview-heading');
    var sub    = document.getElementById('ib-preview-sub');
    var mobile = document.querySelector('.ib-mobile-cta:not(.ib-mobile-cta--ghost)');
    if (wrap) wrap.classList.remove('is-reveal');
    if (buy)  buy.style.display = 'none';   /* hide immediately, no fade */
    if (head) head.textContent = 'Your ring is taking shape';
    if (sub)  sub.textContent  = "Every choice you make brings us closer to the ring she'll love.";
    if (mobile) mobile.style.display = 'none';  /* hide mobile Add to Bag while editing */

    /* Leave the reveal scene: drop the scene class, hide the ceremonial title,
       and move the ring gallery back to its home in the right preview panel. */
    var builder = document.querySelector('.ib-builder');
    if (builder) builder.classList.remove('ib-reveal-scene');
    var title = document.getElementById('ib-reveal-title');
    if (title) title.setAttribute('aria-hidden', 'true');
    var viewer = document.querySelector('.ib-viewer');
    if (viewer && _ibViewerHomeParent && viewer.parentNode !== _ibViewerHomeParent) {
      _ibViewerHomeParent.insertBefore(viewer, _ibViewerHomeNext);
    }
  }

  function ibWizardReveal() {
    var s = ibSel();
    /* Confirm all six required choices are complete (size OR complimentary sizer). */
    var sizerEl = document.getElementById('ib-ring-sizer');
    var hasSize = !!s.ring_size || !!(sizerEl && sizerEl.checked);
    if (!(s.bandKey && s.settingKey && s.shapeKey && s.metalKey && s.carat && hasSize)) return;

    var wrap = document.querySelector('.ib-summary-wrap');
    var buy  = document.getElementById('ib-buy-panel');

    /* Ring name + full specification (buy panel) */
    var titleEl = document.getElementById('ib-sum-title');
    if (titleEl) titleEl.textContent = s.shape + ' ' + s.setting;
    var specEl = document.getElementById('ib-reveal-spec');
    if (specEl) specEl.textContent = s.shape + ' ' + s.setting + ' · ' + s.carat + ' · ' + s.metal + ' · ' + s.band;

    /* THE REVEAL SCENE (the climax):
       LEFT  — move the live ring gallery into the inspection host; it is the hero.
       RIGHT — the calm buy panel, top-aligned with the gallery (no video). */
    var viewer = document.querySelector('.ib-viewer');
    var host   = document.getElementById('ib-reveal-gallery-host');
    if (viewer && host && viewer.parentNode !== host) host.appendChild(viewer);

    var builder = document.querySelector('.ib-builder');
    if (builder) builder.classList.add('ib-reveal-scene');

    /* Reveal the ceremonial title above both columns. */
    var title = document.getElementById('ib-reveal-title');
    if (title) title.setAttribute('aria-hidden', 'false');

    /* A6: concierge-only combinations reveal honestly — bespoke quotation,
       no Add to Bag, no misleading price. All other combos are untouched. */
    var conciergeOnly = ibIsConciergeOnly(s);
    if (buy) {
      var ctaBtn  = buy.querySelector('.ib-cta-btn');
      var priceEl = document.getElementById('ib-price-display');
      var vatEl   = document.getElementById('ib-vat-note');
      var noteEl  = document.getElementById('ib-bespoke-note');
      if (conciergeOnly) {
        if (ctaBtn)  ctaBtn.style.display = 'none';
        if (priceEl) { priceEl.textContent = 'By Bespoke Quotation'; priceEl.classList.remove('is-empty'); }
        if (vatEl)   vatEl.style.display = 'none';
        if (!noteEl) {
          noteEl = document.createElement('p');
          noteEl.id = 'ib-bespoke-note';
          noteEl.style.cssText = 'font-size:1.25rem;line-height:1.6;opacity:0.85;margin:0.6rem 0 0;';
          noteEl.textContent = 'This combination is crafted as a bespoke commission. Our concierge will prepare a personal quotation — we reply within 24 hours.';
          var ccBtn = buy.querySelector('.ib-cta-concierge');
          if (ccBtn && ccBtn.parentNode) ccBtn.parentNode.insertBefore(noteEl, ccBtn);
        }
        noteEl.style.display = '';
      } else {
        if (ctaBtn) ctaBtn.style.display = '';
        if (noteEl) noteEl.style.display = 'none';
      }
    }

    /* Show the buy panel with a calm fade (is-reveal drives the opacity transition). */
    if (buy) buy.style.display = '';
    if (wrap) requestAnimationFrame(function () { wrap.classList.add('is-reveal'); });

    /* Restore mobile bar Add to Bag now that the ring is complete —
       unless this combination is concierge-only (A6). */
    var mobile = document.querySelector('.ib-mobile-cta:not(.ib-mobile-cta--ghost)');
    if (mobile) mobile.style.display = conciergeOnly ? 'none' : '';

    /* Bring the ceremonial title into view first — the moment of arrival. */
    var scrollTarget = title || document.getElementById('ib-builder-grid');
    if (scrollTarget) scrollTarget.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });

    ibWizardUpdateProgress();
  }

  function ibWizardShowReward(n) {
    /* Show a brief emotional reward line on the step */
    var step = document.querySelector('.ib-step--active');
    if (!step) return;
    var existing = step.querySelector('.ib-step-reward');
    if (!existing) {
      existing = document.createElement('p');
      existing.className = 'ib-step-reward';
      step.appendChild(existing);
    }
    existing.textContent = IB_STEP_REWARDS[n] || '';
    existing.style.display = 'block';
    existing.style.opacity = '0';
    setTimeout(function () { existing.style.opacity = '1'; }, 50);
  }

  /* Add shake keyframe to document if not present */
  (function () {
    if (document.getElementById('ib-shake-style')) return;
    var s = document.createElement('style');
    s.id  = 'ib-shake-style';
    s.textContent = '@keyframes ib-shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }';
    document.head.appendChild(s);
  })();

  /* Wire up progress circle click handlers after DOM ready */
  function ibWizardBindProgress() {
    document.querySelectorAll('.ib-progress-step').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var n = parseInt(btn.getAttribute('data-step'), 10);
        if (!isNaN(n) && btn.getAttribute('data-completed') !== null) {
          ibWizardProgressClick(n);
        }
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SECTION 16 — BOOT SEQUENCE
     1. Render static UI (immediate)
     2. Insert mobile sticky preview
     3. Show non-destructive loading overlay
     4. Fetch all 64 products in parallel
     5. On ALL success: build maps → preflight → hide overlay → activate
     6. On ANY failure after retry: keep UI visible, show blocked overlay
  ═══════════════════════════════════════════════════════════════════════ */

  ibRenderOptions();
  try { var _im = document.querySelector('input[name="ib_metal"]:checked'); ibUpdateCardMetal(_im ? _im.dataset.key : null); } catch (e) {}
  try { Object.keys(IB_CARD_WG).forEach(function (k) { var _p = new Image(); _p.src = IB_CARD_WG[k]; }); } catch (e) {}
  ibInsertMobilePreview();
  ibShowLoadingState();

  function ibAttachInputListeners() {
    document.querySelectorAll('input[name="ib_band"],input[name="ib_setting"],input[name="ib_shape"],input[name="ib_metal"],input[name="ib_carat"]').forEach(function (r) {
      if (r.dataset.ibBound) return;
      r.dataset.ibBound = '1';
      r.addEventListener('change', ibUpdate);
    });

    ['ib-mount-band', 'ib-mount-setting', 'ib-mount-shape'].forEach(function (mountId) {
      var mount = document.getElementById(mountId);
      if (!mount || mount.dataset.ibHoverBound) return;
      mount.dataset.ibHoverBound = '1';
      mount.addEventListener('mouseenter', function (e) { ibPlaySwatchVideo(e.target.closest('.ib-option-card')); }, true);
      mount.addEventListener('mouseleave', function (e) { ibPauseSwatchVideo(e.target.closest('.ib-option-card')); }, true);
    });

    var szEl = document.getElementById('ib-ring-size');
    var srEl = document.getElementById('ib-ring-sizer');
    if (szEl && !szEl.dataset.ibBound) {
      szEl.dataset.ibBound = '1';
      szEl.addEventListener('change', function () { ibPersistSelection(); ibUpdateMobilePreview(); });
    }
    if (srEl && !srEl.dataset.ibBound) {
      srEl.dataset.ibBound = '1';
      /* M-03: a sizer request is a flag on the order, not a detour. It rides
         to checkout as the 'Free Sizer Requested' property and ships to the
         checkout address — never open the concierge overlay from here. */
      srEl.addEventListener('change', function () {
        ibPersistSelection();
        ibUpdateMobilePreview();
      });
    }
  }

  ibAttachInputListeners();
  ibSetDefaultPreview();
  ibFetchManifest();   /* v7.3: start manifest fetch in parallel with product fetches */

  ibPositionMobilePreview();
  setTimeout(ibPositionMobilePreview, 300);

  window.addEventListener('resize', function () {
    _ibPreviewTopLocked = false;
    ibPositionMobilePreview();
  });

  var ibScrollTick = false;
  window.addEventListener('scroll', function () {
    if (!ibScrollTick) {
      requestAnimationFrame(function () {
        if (!_ibPreviewTopLocked) ibPositionMobilePreview();
        ibUpdateMobilePreviewFade();
        ibScrollTick = false;
      });
      ibScrollTick = true;
    }
  }, { passive: true });

  setTimeout(ibUpdateMobilePreviewFade, 400);

  /* ─────────────────────────────────────────────────────────────────────
     PRODUCT FETCH STRATEGY — locked rule:
       "Keep fast load. Avoid edge-case throttling."
       "Fallback to batching only if browser throttling detected."

     Strategy:
       Stage 1: Try all 64 handles in parallel via Promise.all.
                Fastest path, ~600–1200ms cold.
       Stage 2: If Stage 1 detects throttling signals (HTTP 429, or any
                handle's retry also failed), retry the FAILED handles
                only, in batches of 16, sequentially. Successful handles
                from Stage 1 are kept.

     Throttling detection: ibFetchProduct already retries once on
     failure. A handle reaching here as ok:false has already been
     retried once. For Stage 1 we accept that as a real failure.
     Stage 2 gives those handles one final chance using a slower,
     batched approach that's friendlier to rate-limited storefronts.

     If after Stage 2 anything is still failing → CASE A (blocked).
  ───────────────────────────────────────────────────────────────────── */

  function ibFetchAllParallel(handles) {
    return Promise.all(handles.map(function (handle) {
      return ibFetchProduct(handle)
        .then(function (product) { return { ok: true,  handle: handle, product: product }; })
        .catch(function ()       { return { ok: false, handle: handle, product: null    }; });
    }));
  }

  /* Batched sequential fetch — used only as throttling fallback. */
  function ibFetchInBatches(handles, batchSize) {
    var results = [];
    var index = 0;

    function nextBatch() {
      if (index >= handles.length) {
        return Promise.resolve(results);
      }
      var slice = handles.slice(index, index + batchSize);
      index += batchSize;

      return Promise.all(slice.map(function (handle) {
        return ibFetchProduct(handle)
          .then(function (product) { return { ok: true,  handle: handle, product: product }; })
          .catch(function ()       { return { ok: false, handle: handle, product: null    }; });
      })).then(function (batchResults) {
        results = results.concat(batchResults);
        /* Brief pause between batches gives rate-limited servers
           breathing room. 250ms is conservative but invisible to
           the customer behind the loading overlay. */
        return new Promise(function (resolve) {
          setTimeout(function () { nextBatch().then(resolve); }, 250);
        });
      });
    }

    return nextBatch();
  }

  /* Run Stage 1, then Stage 2 only if needed. */
  ibFetchAllParallel(PRODUCT_HANDLES).then(function (stage1) {
    var failed = stage1.filter(function (r) { return !r.ok; });

    /* If Stage 1 cleared everything, proceed straight away. */
    if (failed.length === 0) {
      return stage1;
    }

    /* Throttling fallback — retry failed handles in batches of 16. */
    console.info('[IB Builder] ' + failed.length + ' handle(s) failed Stage 1 — retrying in batches of 16.');
    var failedHandles = failed.map(function (r) { return r.handle; });
    var succeeded     = stage1.filter(function (r) { return r.ok; });

    return ibFetchInBatches(failedHandles, 16).then(function (stage2) {
      return succeeded.concat(stage2);
    });
  }).then(function (results) {
    var allOk = results.every(function (r) { return r.ok; });

    /* CASE A — system failure: at least one handle failed both stages */
    if (!allOk) {
      ibPreflightCheck(results);
      ibShowBlockedState();
      return;
    }

    /* All fetches succeeded — data trusted. Build maps and activate. */
    ibBuildMaps(results);
    ibPreflightCheck(results);

    ibDataReady = true;
    ibHideLoadingState();
    ibRestoreDesignFromUrl();  /* Restore URL design state before first update */
    ibUpdate();
    ibWizardInit();
    ibWizardApplyRestoredDesign();  /* B3: jump a shared design to the Size step */
    ibWizardBindProgress();
  });

})();
